import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMarketNavigate } from '../hooks/useMarketNavigate';
import { MarketLink } from '../components/MarketLink';
import { clearSession } from '../utils/auth';
import { getRestaurants, getAggregatorSources, updateAggregatorSource, getSettings, updateSetting, getRestaurantsForBase, getSettingsForBase, getAggregatorSourcesForBase, updateSettingForBase, getCustomKeywordsForBase, saveCustomKeywordsForBase, clearAirtableCache } from '../services/airtable';
import { FOOD_KEYWORDS, DRINK_KEYWORDS, setCustomKeywords } from '../utils/descriptionParser';
import { useMarket } from '../context/MarketContext';
import { useAuth } from '../context/AuthContext';
import { getAllMarkets } from '../config/markets';
import SEOHead from '../components/SEOHead';
import { StatCard, AnalyticsLineChart, LeaderboardTable } from '../components/analytics';
import VenueEditor from '../components/VenueEditor';
import './AdminConsole.css';

// ═══════════════════════════════════════════════════════════════
// PIE CHART COMPONENT
// ═══════════════════════════════════════════════════════════════
function PieChart({ title, data, colors, footnote, placeholder }) {
  // Convert data object to array and calculate total
  const entries = Object.entries(data).filter(([_, value]) => value > 0);
  const total = entries.reduce((sum, [_, value]) => sum + value, 0);

  if (total === 0 || placeholder) {
    return (
      <div className="pie-chart-card">
        <h4 className="pie-chart-title">{title}</h4>
        <div className="pie-chart-wrapper">
          <svg className="pie-chart-svg" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="20" />
          </svg>
          <div className="pie-chart-center-label">
            <span className="pie-chart-center-value">—</span>
            <span className="pie-chart-center-text">N/A</span>
          </div>
        </div>
        <div className="pie-chart-legend">
          <div className="pie-chart-legend-item">
            <span className="pie-chart-legend-color" style={{ background: '#9ca3af' }}></span>
            <span className="pie-chart-legend-label">No data yet</span>
          </div>
        </div>
      </div>
    );
  }

  // Calculate segments
  let cumulativePercent = 0;
  const segments = entries.map(([label, value], index) => {
    const percent = (value / total) * 100;
    const startPercent = cumulativePercent;
    cumulativePercent += percent;

    // SVG circle math: circumference = 2 * PI * r = 2 * 3.14159 * 40 ≈ 251.3
    const circumference = 251.3;
    const strokeDasharray = `${(percent / 100) * circumference} ${circumference}`;
    const strokeDashoffset = -((startPercent / 100) * circumference);

    return {
      label,
      value,
      percent,
      color: colors[index % colors.length],
      strokeDasharray,
      strokeDashoffset
    };
  });

  return (
    <div className="pie-chart-card">
      <h4 className="pie-chart-title">{title}</h4>
      <div className="pie-chart-wrapper">
        <svg className="pie-chart-svg" viewBox="0 0 100 100">
          {segments.map((segment, index) => (
            <circle
              key={index}
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={segment.color}
              strokeWidth="20"
              strokeDasharray={segment.strokeDasharray}
              strokeDashoffset={segment.strokeDashoffset}
              style={{ transition: 'stroke-dasharray 0.3s ease' }}
            />
          ))}
        </svg>
        <div className="pie-chart-center-label">
          <span className="pie-chart-center-value">{total}</span>
          <span className="pie-chart-center-text">Total</span>
        </div>
      </div>
      <div className="pie-chart-legend">
        {segments.map((segment, index) => (
          <div key={index} className="pie-chart-legend-item">
            <span className="pie-chart-legend-color" style={{ background: segment.color }}></span>
            <span className="pie-chart-legend-label">{segment.label}</span>
            <span className="pie-chart-legend-value">{segment.value} ({Math.round(segment.percent)}%)</span>
          </div>
        ))}
      </div>
      {footnote && <div className="pie-chart-footnote">{footnote}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// STALE CONTENT LIST COMPONENT
// ═══════════════════════════════════════════════════════════════
function StaleVenuesList({ venues, thresholds }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getDaysClass = (daysAgo) => {
    if (daysAgo >= 30) return 'critical';
    if (daysAgo >= 14) return 'warning';
    return 'notice';
  };

  return (
    <div className="stale-venues-section">
      <div className="stale-venues-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="stale-venues-header-left">
          <span className="stale-venues-icon">⚠️</span>
          <span className="stale-venues-title">Stale Content Alert</span>
          <span className="stale-venues-count">{venues.length} venue{venues.length !== 1 ? 's' : ''} with stale data</span>
        </div>
        <button className="collapse-button">{isExpanded ? '▼' : '▶'}</button>
      </div>

      {isExpanded && (
        <div className="stale-venues-body">
          <p className="stale-venues-description">
            Venues with content that hasn't been updated recently. Thresholds: Events {thresholds?.events || 7}d, Specials {thresholds?.specials || 7}d, Happy Hours {thresholds?.happyHours || 30}d
          </p>
          {venues.length === 0 ? (
            <div className="stale-venues-empty">
              <div className="stale-venues-empty-icon">✅</div>
              <p>All content is fresh!</p>
            </div>
          ) : (
            <div className="stale-venues-table-wrapper">
              <table className="stale-venues-table">
                <thead>
                  <tr>
                    <th>Venue</th>
                    <th>Stale Content</th>
                    <th>Most Stale</th>
                  </tr>
                </thead>
                <tbody>
                  {venues.map((venue) => (
                    <tr key={venue.id}>
                      <td className="stale-venue-name">{venue.name}</td>
                      <td className="stale-venue-types">
                        {venue.staleTypes?.map((type, i) => (
                          <span key={i} className="stale-type-badge">{type}</span>
                        )) || 'Unknown'}
                      </td>
                      <td className={`stale-venue-days ${getDaysClass(venue.daysAgo)}`}>
                        {venue.daysAgo} days
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HELPER: Format event date for preview, handling date-only values
// ═══════════════════════════════════════════════════════════════
function formatPreviewDate(dateStr) {
  if (!dateStr) return '';
  // Date-only: "YYYY-MM-DD" (10 chars, no T) — show date without time
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = new Date(isDateOnly ? dateStr + 'T12:00:00' : dateStr);
  if (isNaN(d.getTime())) return '';
  if (isDateOnly) {
    return d.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
  }
  return d.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

// ═══════════════════════════════════════════════════════════════
// MAIN ADMIN CONSOLE COMPONENT
// ═══════════════════════════════════════════════════════════════
function AdminConsole() {
  const navigate = useMarketNavigate();
  const [searchParams] = useSearchParams();
  const market = useMarket();
  const { user: authUser, logout: authLogout, getToken, isAuthenticated } = useAuth();
  const terminalBodyRef = useRef(null);
  const avatarDropdownRef = useRef(null);
  const [avatarDropdownOpen, setAvatarDropdownOpen] = useState(false);

  // Close avatar dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (avatarDropdownRef.current && !avatarDropdownRef.current.contains(event.target)) {
        setAvatarDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Role-based visibility flags
  const useNewAuth = market.features?.venueOwnerAccounts === true;
  const userRole = useNewAuth ? (authUser?.role || 'venue_owner') : 'admin'; // Legacy = always admin
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;
  const isVenueOwner = userRole === 'venue_owner';

  // Market switcher (Super Admin only)
  const allMarkets = getAllMarkets();
  const [adminMarketId, setAdminMarketId] = useState(searchParams.get('market') || market.id);
  const activeAdminMarket = isSuperAdmin ? (allMarkets[adminMarketId] || market) : market;
  const activeBaseId = activeAdminMarket.airtableBaseId;

  /**
   * Build fetch headers with auth token for backend API calls.
   * Returns headers object that includes Authorization bearer token
   * when new auth is active, otherwise returns basic headers.
   */
  const getAuthHeaders = useCallback((contentType = 'application/json') => {
    const headers = {};
    if (contentType) headers['Content-Type'] = contentType;
    if (useNewAuth) {
      const token = getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [useNewAuth, getToken]);

  // Collapsible section states (all collapsed by default except console)
  const [isScraperSectionExpanded, setIsScraperSectionExpanded] = useState(false);
  const [isContentUploadExpanded, setIsContentUploadExpanded] = useState(false);
  const [isToolsSettingsExpanded, setIsToolsSettingsExpanded] = useState(false);
  const [isUserMgmtExpanded, setIsUserMgmtExpanded] = useState(false);
  const [isContentReviewExpanded, setIsContentReviewExpanded] = useState(false);
  const [isAuditLogExpanded, setIsAuditLogExpanded] = useState(false);
  const [isAnalyticsExpanded, setIsAnalyticsExpanded] = useState(false);

  // GA4 Analytics state (market-scoped)
  const [acAnalyticsPeriod, setAcAnalyticsPeriod] = useState('7d');
  const [acTraffic, setAcTraffic] = useState(null);
  const [acTopVenues, setAcTopVenues] = useState(null);
  const [acTrafficLoading, setAcTrafficLoading] = useState(false);
  const [acTopVenuesLoading, setAcTopVenuesLoading] = useState(false);
  const [acAnalyticsError, setAcAnalyticsError] = useState(null);

  // Content review state
  const [reviewSubmissions, setReviewSubmissions] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('pending'); // pending | approved | rejected | revision_requested | all
  const [reviewPendingCount, setReviewPendingCount] = useState(0);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewActionLoading, setReviewActionLoading] = useState(null); // submission ID being acted on

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditFilterAction, setAuditFilterAction] = useState('');
  const [auditFilterMarket, setAuditFilterMarket] = useState('');
  const [auditDateStart, setAuditDateStart] = useState('');
  const [auditDateEnd, setAuditDateEnd] = useState('');
  const [auditOffset, setAuditOffset] = useState(null);
  const [auditHasMore, setAuditHasMore] = useState(false);

  // Venue editor state
  const [isVenueEditorExpanded, setIsVenueEditorExpanded] = useState(false);

  // Venue tier management state
  const [isVenueTierExpanded, setIsVenueTierExpanded] = useState(false);
  const [vtVenues, setVtVenues] = useState([]);
  const [vtLoading, setVtLoading] = useState(false);
  const [vtError, setVtError] = useState(null);
  const [vtEdits, setVtEdits] = useState({}); // { [venueId]: { tier, priority, isFeatured } }
  const [vtSaving, setVtSaving] = useState(null); // venue ID being saved

  // User management state
  const [userMgmtUsers, setUserMgmtUsers] = useState([]);
  const [userMgmtLoading, setUserMgmtLoading] = useState(false);
  const [userMgmtError, setUserMgmtError] = useState(null);
  const [userMgmtView, setUserMgmtView] = useState('list'); // 'list' | 'create' | 'edit'
  const [userMgmtEditTarget, setUserMgmtEditTarget] = useState(null);
  const [userMgmtFilterRole, setUserMgmtFilterRole] = useState('');
  const [userMgmtFilterStatus, setUserMgmtFilterStatus] = useState('');
  // Create/Edit form fields
  const [umFormName, setUmFormName] = useState('');
  const [umFormEmail, setUmFormEmail] = useState('');
  const [umFormPassword, setUmFormPassword] = useState('');
  const [umFormRole, setUmFormRole] = useState('venue_owner');
  const [umFormMarkets, setUmFormMarkets] = useState([]);
  const [umFormTier, setUmFormTier] = useState('free');
  const [umFormVenues, setUmFormVenues] = useState('');
  const [umFormNotes, setUmFormNotes] = useState('');
  const [umFormStatus, setUmFormStatus] = useState('active');
  const [umFormSaving, setUmFormSaving] = useState(false);
  const [umFormError, setUmFormError] = useState('');

  // Tab state (within sections)
  const [activeScraperTab, setActiveScraperTab] = useState('websites');
  const [activeUploadTab, setActiveUploadTab] = useState('photos');

  // Website scraper state
  const [scrapeScope, setScrapeScope] = useState('all');
  const [scrapeNoCache, setScrapeNoCache] = useState(false);
  const [scrapeUseVision, setScrapeUseVision] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [websitePreviewItems, setWebsitePreviewItems] = useState(null);
  const [websiteSelectedItems, setWebsiteSelectedItems] = useState({});
  const [isSavingWebsiteItems, setIsSavingWebsiteItems] = useState(false);
  const [websiteItemTypeOverrides, setWebsiteItemTypeOverrides] = useState({});

  // Aggregator scraper state
  const [aggregatorSources, setAggregatorSources] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState(''); // '' = manual, 'all' = scrape all, or record ID
  const [aggregatorUrl, setAggregatorUrl] = useState('');
  const [isScrapingAggregator, setIsScrapingAggregator] = useState(false);
  const [aggregatorResult, setAggregatorResult] = useState(null);
  const [aggregatorProgress, setAggregatorProgress] = useState(null); // For "Scrape All" progress
  // Aggregator preview state
  const [aggregatorPreviewEvents, setAggregatorPreviewEvents] = useState(null);
  const [aggregatorSelectedEvents, setAggregatorSelectedEvents] = useState({});
  const [isSavingAggregatorEvents, setIsSavingAggregatorEvents] = useState(false);

  // Social photo upload state
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]); // Files selected but not yet added to queue
  const [uploadQueue, setUploadQueue] = useState([]); // [{restaurantId, restaurantName, files: [File, ...]}]
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedExtractions, setSelectedExtractions] = useState({});
  const [extractionTypeOverrides, setExtractionTypeOverrides] = useState({});

  // Social text upload state
  const [textRestaurant, setTextRestaurant] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [isProcessingText, setIsProcessingText] = useState(false);
  const [textExtractedData, setTextExtractedData] = useState(null);
  const [textSelectedExtractions, setTextSelectedExtractions] = useState({});
  const [textExtractionTypeOverrides, setTextExtractionTypeOverrides] = useState({});

  // Shared console output
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [isConsoleExpanded, setIsConsoleExpanded] = useState(true);
  const [consoleSource, setConsoleSource] = useState(null); // 'scraper' | 'upload' | null

  // Database metrics
  const [dbMetrics, setDbMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);

  // Tools state
  const [isRunningTool, setIsRunningTool] = useState(false);
  const [runningToolType, setRunningToolType] = useState(null); // Track which specific tool is running
  const [lastToolType, setLastToolType] = useState(null); // Remember which tool produced the console output
  const [toolVerbose, setToolVerbose] = useState(false);
  const [cleanupDryRun, setCleanupDryRun] = useState(true); // Default to dry run for safety
  const [toolConsoleOutput, setToolConsoleOutput] = useState([]);
  const [isToolConsoleExpanded, setIsToolConsoleExpanded] = useState(true);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [isDatabaseToolsExpanded, setIsDatabaseToolsExpanded] = useState(false);
  const toolConsoleRef = useRef(null);
  const specialScraperConsoleRef = useRef(null);

  // Settings state
  const [settings, setSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsEdits, setSettingsEdits] = useState({});
  const [settingsSaving, setSettingsSaving] = useState({});

  // Keyword Manager state
  const [customFoodKeywords, setCustomFoodKeywords] = useState([]);
  const [customDrinkKeywords, setCustomDrinkKeywords] = useState([]);
  const [foodKeywordRecordId, setFoodKeywordRecordId] = useState(null);
  const [drinkKeywordRecordId, setDrinkKeywordRecordId] = useState(null);
  const [keywordInput, setKeywordInput] = useState({ food: '', drink: '' });
  const [keywordSaving, setKeywordSaving] = useState(false);
  const [isKeywordManagerExpanded, setIsKeywordManagerExpanded] = useState(false);

  // Unified console log function
  const addConsoleLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();

    // Filter out verbose logging prefixes but keep the important parts
    let cleanMessage = message;
    const prefixes = ['INFO:__main__:', 'INFO:web_scraper:', 'INFO:claude_extractor:', 'INFO:httpx:', 'ERROR:web_scraper:', 'ERROR:__main__:', 'WARNING:__main__:'];
    for (const prefix of prefixes) {
      if (cleanMessage.startsWith(prefix)) {
        cleanMessage = cleanMessage.substring(prefix.length);
        break;
      }
    }

    // Skip heartbeat and very verbose API logs
    if (cleanMessage.includes('HTTP Request: POST https://api.anthropic.com') ||
        cleanMessage.includes('Calling Claude API to extract') ||
        cleanMessage.startsWith('Claude raw response:')) {
      return; // Skip these verbose logs
    }

    // Skip empty lines and separator lines
    if (!cleanMessage.trim() || cleanMessage.match(/^[=]+$/)) {
      return;
    }

    setConsoleOutput(prev => [...prev, { timestamp, message: cleanMessage, type }]);
  }, []);

  // Auto-scroll terminal to bottom when new output is added
  useEffect(() => {
    if (terminalBodyRef.current && isConsoleExpanded) {
      terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
    }
  }, [consoleOutput, isConsoleExpanded]);

  // Auto-scroll tools console
  useEffect(() => {
    if (toolConsoleRef.current && isToolConsoleExpanded) {
      toolConsoleRef.current.scrollTop = toolConsoleRef.current.scrollHeight;
    }
  }, [toolConsoleOutput, isToolConsoleExpanded]);

  // Fetch database metrics and restaurants — re-fetches when market changes
  useEffect(() => {
    const fetchMetrics = async () => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      try {
        setMetricsLoading(true);
        setMetricsError(null);
        const response = await fetch(`${apiUrl}/api/metrics?baseId=${activeBaseId}`, {
          headers: getAuthHeaders(null),
        });
        const data = await response.json();

        if (data.success) {
          setDbMetrics(data);
        } else {
          setMetricsError(data.error || 'Failed to fetch metrics');
        }
      } catch (error) {
        setMetricsError(error.message);
      } finally {
        setMetricsLoading(false);
      }
    };

    const fetchRestaurants = async () => {
      try {
        const data = await getRestaurantsForBase(activeBaseId);
        // Transform Airtable records to flatten fields, filter to active, and sort by name
        const activeRestaurants = data
          .map(r => ({ id: r.id, ...r.fields }))
          .filter(r => r.Status === 'Active')
          .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
        setRestaurants(activeRestaurants);
      } catch (error) {
        console.error('Failed to fetch restaurants:', error);
      }
    };

    const fetchAggregatorSources = async () => {
      try {
        const data = await getAggregatorSourcesForBase(activeBaseId);
        setAggregatorSources(data);
      } catch (error) {
        console.error('Failed to fetch aggregator sources:', error);
      }
    };

    const fetchSettingsData = async () => {
      try {
        setSettingsLoading(true);
        const data = await getSettingsForBase(activeBaseId);
        setSettings(data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };

    const fetchCustomKeywords = async () => {
      try {
        const kw = await getCustomKeywordsForBase(activeBaseId);
        setCustomFoodKeywords(kw.food);
        setCustomDrinkKeywords(kw.drink);
        setFoodKeywordRecordId(kw.foodRecordId);
        setDrinkKeywordRecordId(kw.drinkRecordId);
        setCustomKeywords(kw.food, kw.drink);
      } catch (error) {
        console.error('Failed to fetch custom keywords:', error);
      }
    };

    fetchMetrics();
    fetchRestaurants();
    fetchAggregatorSources();
    fetchSettingsData();
    fetchCustomKeywords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBaseId]);

  // Fetch GA4 analytics when analytics section is expanded
  useEffect(() => {
    if (!isAnalyticsExpanded || !isAdmin) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const headers = getAuthHeaders();
    const marketId = activeAdminMarket.id;

    setAcTrafficLoading(true);
    fetch(`${apiUrl}/api/analytics/traffic?scope=market&marketId=${marketId}&period=${acAnalyticsPeriod}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed')))
      .then(d => { setAcTraffic(d); setAcAnalyticsError(null); })
      .catch(err => setAcAnalyticsError(err.message))
      .finally(() => setAcTrafficLoading(false));

    setAcTopVenuesLoading(true);
    fetch(`${apiUrl}/api/analytics/top-venues?scope=market&marketId=${marketId}&limit=10&period=${acAnalyticsPeriod}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed')))
      .then(d => { setAcTopVenues(d); })
      .catch(() => {})
      .finally(() => setAcTopVenuesLoading(false));
  }, [isAnalyticsExpanded, isAdmin, acAnalyticsPeriod, activeAdminMarket.id, getAuthHeaders]);

  // Helper: get effective item type (with override support)
  const getItemType = (item) => websiteItemTypeOverrides[item._index] || item.item_type;

  // Helper: remap Airtable field names when changing item type
  const remapFields = (data, fromType, toType) => {
    const remapped = { ...data };
    const nameFields = { deal: 'Deal Name', special: 'Special Name', event: 'Event Name' };
    const fromField = nameFields[fromType];
    const toField = nameFields[toType];
    if (fromField && toField && remapped[fromField]) {
      remapped[toField] = remapped[fromField];
      delete remapped[fromField];
    }
    return remapped;
  };

  // Photo/Text upload helpers: extracted items live in pre-categorized buckets
  // (happyHours / events / specials) rather than a flat array with item_type.
  // Expose deal/special/event as the override values so the UI matches the
  // website scraper; map between bucket key ('hh'|'event'|'special') and
  // override value ('deal'|'event'|'special').
  const BUCKET_BY_TYPE = { deal: 'happyHours', event: 'events', special: 'specials' };
  const bucketKeyToType = (bucketKey) => (bucketKey === 'hh' ? 'deal' : bucketKey);
  const getExtractionType = (overrides, restaurantId, bucketKey, idx) => {
    const key = `${restaurantId}-${bucketKey}-${idx}`;
    return overrides[key] || bucketKeyToType(bucketKey);
  };

  const handleManualScrape = async () => {
    setIsScraping(true);
    setScrapeResult(null);
    setWebsitePreviewItems(null);
    setWebsiteSelectedItems({});
    setConsoleOutput([]);
    setIsConsoleExpanded(true);
    setConsoleSource('scraper');

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    console.log('Starting scrape request to:', `${apiUrl}/api/scrape`);
    console.log('Scope:', scrapeScope);

    try {
      // Create EventSource for Server-Sent Events
      const response = await fetch(`${apiUrl}/api/scrape`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ scope: scrapeScope, baseId: activeBaseId, noCache: scrapeNoCache, preview: true, useVision: scrapeUseVision })
      });

      console.log('Response received:', response.status, response.statusText);

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Check if response body exists
      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = ''; // Buffer for reassembling SSE messages split across chunks

      console.log('Starting to read stream...');

      try {
        while (true) {
        console.log('Reading chunk...');
        const { value, done } = await reader.read();
        console.log('Chunk read:', { done, valueLength: value?.length });

        if (done) {
          console.log('Stream completed');
          break;
        }

        const chunk = decoder.decode(value);
        console.log('Decoded chunk:', chunk.substring(0, 100));
        sseBuffer += chunk;

        // Split on double newline (SSE message boundary) — keep last segment as partial
        const messages = sseBuffer.split('\n\n');
        sseBuffer = messages.pop(); // Last segment may be incomplete; keep in buffer

        for (const line of messages) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              console.log('Parsed SSE data:', data.type, data.message?.substring(0, 50));

              if (data.type === 'complete') {
                // Parse metrics from error log if present
                let metrics = null;
                if (data.errorLog) {
                  const sourcesMatch = data.errorLog.match(/Sources processed: (\d+)/);
                  const successMatch = data.errorLog.match(/✓ Successful: (\d+)/);
                  const failedMatch = data.errorLog.match(/✗ Failed: (\d+)/);
                  const itemsMatch = data.errorLog.match(/Total items extracted: (\d+)/);

                  if (sourcesMatch || successMatch || failedMatch || itemsMatch) {
                    metrics = {
                      sourcesChecked: sourcesMatch ? parseInt(sourcesMatch[1]) : 0,
                      successful: successMatch ? parseInt(successMatch[1]) : 0,
                      failed: failedMatch ? parseInt(failedMatch[1]) : 0,
                      totalItems: itemsMatch ? parseInt(itemsMatch[1]) : 0
                    };
                  }
                }

                // Handle preview items from website scraper
                if (data.previewItems && data.previewItems.length > 0) {
                  setWebsitePreviewItems(data.previewItems);
                  const selections = {};
                  data.previewItems.forEach((_, i) => { selections[`ws-item-${i}`] = true; });
                  setWebsiteSelectedItems(selections);
                  setWebsiteItemTypeOverrides({});
                }

                setScrapeResult({
                  status: data.hasErrors ? 'error' : 'completed',
                  message: data.message,
                  errorLog: data.errorLog,
                  metrics: metrics
                });
                setIsScraping(false);
              } else {
                addConsoleLog(data.message, data.type);
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e, 'Line:', line.substring(0, 200));
            }
          }
        }
        }
      } catch (streamError) {
        console.error('Stream reading error:', streamError);
        addConsoleLog(`Stream error: ${streamError.message}`, 'error');
        throw streamError;
      }
    } catch (error) {
      console.error('Top-level error:', error);
      addConsoleLog(`Failed to connect to scraper API: ${error.message}`, 'error');
      setScrapeResult({
        status: 'error',
        message: 'Failed to connect to scraper API',
        errorLog: error.message
      });
      setIsScraping(false);
    }
  };

  // Scrape a single aggregator URL and return results
  const scrapeSingleAggregator = async (url, sourceRecord = null) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    let eventsFound = 0;
    let hasErrors = false;

    try {
      const response = await fetch(`${apiUrl}/api/scrape-aggregator`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ url, dryRun: false, preview: true, baseId: activeBaseId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = ''; // Buffer for reassembling SSE messages split across chunks

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        sseBuffer += chunk;
        const messages = sseBuffer.split('\n\n');
        sseBuffer = messages.pop(); // Last segment may be incomplete

        for (const line of messages) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));

              if (data.type === 'complete') {
                // Check for preview events
                if (data.previewEvents && data.previewEvents.length > 0) {
                  eventsFound = data.previewEvents.length;
                  return { success: true, eventsFound, errorLog: data.errorLog, previewEvents: data.previewEvents };
                }

                // Parse metrics from error log if present
                if (data.errorLog) {
                  const eventsMatch = data.errorLog.match(/Events Created: (\d+)/);
                  const extractedMatch = data.errorLog.match(/Extracted (\d+) event/);
                  eventsFound = eventsMatch ? parseInt(eventsMatch[1]) :
                               (extractedMatch ? parseInt(extractedMatch[1]) : 0);
                }
                hasErrors = data.hasErrors;

                // Update Airtable source record if provided (and not preview mode)
                // Source status updated after user saves from preview

                return { success: !hasErrors, eventsFound, errorLog: data.errorLog };
              } else {
                addConsoleLog(data.message, data.type);
                if (data.type === 'error') {
                  hasErrors = true;
                }
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }

      return { success: true, eventsFound, errorLog: '' };
    } catch (error) {
      addConsoleLog(`Error: ${error.message}`, 'error');

      return { success: false, eventsFound: 0, errorLog: error.message };
    }
  };

  const handleAggregatorScrape = async () => {
    setIsScrapingAggregator(true);
    setAggregatorResult(null);
    setAggregatorProgress(null);
    setAggregatorPreviewEvents(null);
    setAggregatorSelectedEvents({});
    setConsoleOutput([]);
    setIsConsoleExpanded(true);
    setConsoleSource('scraper');

    try {
      if (selectedSourceId === 'all') {
        // Scrape All Active Sources
        const activeSources = aggregatorSources.filter(s => s.fields.Active);
        const totalSources = activeSources.length;
        let completedSources = 0;
        let totalEventsFound = 0;
        let failedSources = [];

        addConsoleLog(`Starting batch scrape of ${totalSources} sources...`, 'info');

        let allPreviewEvents = [];

        for (const source of activeSources) {
          completedSources++;
          const sourceName = source.fields.Name || 'Unnamed Source';
          const sourceUrl = source.fields.URL;

          setAggregatorProgress({
            current: completedSources,
            total: totalSources,
            currentSource: sourceName
          });

          addConsoleLog(`\n[${ completedSources }/${totalSources}] Processing: ${sourceName}`, 'info');
          addConsoleLog(`URL: ${sourceUrl}`, 'info');

          const result = await scrapeSingleAggregator(sourceUrl, source);

          if (result.success) {
            totalEventsFound += result.eventsFound;
            if (result.previewEvents) {
              allPreviewEvents = allPreviewEvents.concat(result.previewEvents);
            }
            addConsoleLog(`✓ Completed: ${result.eventsFound} events found`, 'success');
          } else {
            failedSources.push(sourceName);
            addConsoleLog(`✗ Failed: ${result.errorLog}`, 'error');
          }
        }

        // Populate preview state if in preview mode
        if (allPreviewEvents.length > 0) {
          setAggregatorPreviewEvents(allPreviewEvents);
          const selections = {};
          allPreviewEvents.forEach((_, i) => { selections[`agg-event-${i}`] = true; });
          setAggregatorSelectedEvents(selections);
        }

        setAggregatorResult({
          status: failedSources.length === 0 ? 'completed' : 'error',
          message: `Batch scrape complete: ${totalSources} sources processed, ${totalEventsFound} total events`,
          metrics: {
            eventsExtracted: totalEventsFound,
            successfullyWritten: 0,
            sourcesProcessed: totalSources,
            sourcesFailed: failedSources.length
          }
        });
      } else {
        // Single source scrape
        let url = aggregatorUrl;
        let sourceRecord = null;

        // If a specific source is selected (not manual entry), get its details
        if (selectedSourceId && selectedSourceId !== 'all') {
          sourceRecord = aggregatorSources.find(s => s.id === selectedSourceId);
          if (sourceRecord) {
            url = sourceRecord.fields.URL;
          }
        }

        addConsoleLog(`Starting scrape...`, 'info');
        addConsoleLog(`URL: ${url}`, 'info');

        const result = await scrapeSingleAggregator(url, sourceRecord);

        // Populate preview state if preview events returned
        if (result.previewEvents && result.previewEvents.length > 0) {
          setAggregatorPreviewEvents(result.previewEvents);
          const selections = {};
          result.previewEvents.forEach((_, i) => { selections[`agg-event-${i}`] = true; });
          setAggregatorSelectedEvents(selections);
        }

        setAggregatorResult({
          status: result.success ? 'completed' : 'error',
          message: result.success
            ? `Scrape completed`
            : `Scrape failed`,
          errorLog: result.errorLog,
          metrics: {
            eventsExtracted: result.eventsFound,
            successfullyWritten: 0
          }
        });
      }
    } catch (error) {
      addConsoleLog(`Failed to connect to scraper API: ${error.message}`, 'error');
      setAggregatorResult({
        status: 'error',
        message: 'Failed to connect to scraper API',
        errorLog: error.message
      });
    } finally {
      setIsScrapingAggregator(false);
      setAggregatorProgress(null);

      // Source status refreshed after user saves from preview
    }
  };

  // Aggregator preview handlers
  const handleToggleAggregatorEvent = (key) => {
    setAggregatorSelectedEvents(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveAggregatorEvents = async () => {
    if (!aggregatorPreviewEvents) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    setIsSavingAggregatorEvents(true);
    addConsoleLog('Saving selected aggregator events to database...', 'info');

    try {
      // Filter to only selected events
      const selectedEvents = aggregatorPreviewEvents.filter((_, i) =>
        aggregatorSelectedEvents[`agg-event-${i}`]
      );

      if (selectedEvents.length === 0) {
        addConsoleLog('No events selected to save.', 'warning');
        setIsSavingAggregatorEvents(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/save-aggregator-events`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ events: selectedEvents, baseId: activeBaseId })
      });

      const result = await response.json();
      if (result.success) {
        const total = (result.savedCount || 0) + (result.updatedCount || 0);
        addConsoleLog(`✓ Saved ${result.savedCount || 0} new + ${result.updatedCount || 0} updated events to database`, 'success');
        // Clear preview state
        setAggregatorPreviewEvents(null);
        setAggregatorSelectedEvents({});
        setAggregatorResult(prev => prev ? {
          ...prev,
          message: `${total} events saved to database`,
          metrics: { ...prev.metrics, successfullyWritten: total }
        } : null);
      } else {
        addConsoleLog(`✗ Failed to save: ${result.error}`, 'error');
      }
    } catch (error) {
      addConsoleLog(`✗ Error saving: ${error.message}`, 'error');
    } finally {
      setIsSavingAggregatorEvents(false);
    }
  };

  const handleDiscardAggregatorPreview = () => {
    setAggregatorPreviewEvents(null);
    setAggregatorSelectedEvents({});
    addConsoleLog('Preview discarded.', 'info');
  };

  // Website scraper preview handlers
  const handleToggleWebsiteItem = (key) => {
    setWebsiteSelectedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSelectAllWebsiteItems = (selectAll) => {
    if (!websitePreviewItems) return;
    const selections = {};
    websitePreviewItems.forEach((_, i) => {
      selections[`ws-item-${i}`] = selectAll;
    });
    setWebsiteSelectedItems(selections);
  };

  const handleSaveWebsiteItems = async () => {
    if (!websitePreviewItems) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    setIsSavingWebsiteItems(true);
    addConsoleLog('Saving selected website items to database...', 'info');

    try {
      // Filter to only selected items, applying any type overrides
      const selectedItems = websitePreviewItems
        .filter((_, i) => websiteSelectedItems[`ws-item-${i}`])
        .map(item => {
          const overriddenType = websiteItemTypeOverrides[item._index];
          if (overriddenType && overriddenType !== item.item_type) {
            return {
              ...item,
              item_type: overriddenType,
              item_data: remapFields(item.item_data, item.item_type, overriddenType)
            };
          }
          return item;
        });

      if (selectedItems.length === 0) {
        addConsoleLog('No items selected to save.', 'warning');
        setIsSavingWebsiteItems(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/save-website-items`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ items: selectedItems, baseId: activeBaseId })
      });

      const result = await response.json();
      if (result.success) {
        const total = (result.savedCount || 0) + (result.updatedCount || 0);
        addConsoleLog(`✓ Saved ${result.savedCount || 0} new + ${result.updatedCount || 0} updated items to database`, 'success');
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach(err => addConsoleLog(`⚠ ${err}`, 'warning'));
        }
        // Clear preview state
        setWebsitePreviewItems(null);
        setWebsiteSelectedItems({});
        setScrapeResult(prev => prev ? {
          ...prev,
          message: `${total} items saved to database`,
          metrics: { ...prev.metrics, totalItems: total }
        } : null);
      } else {
        addConsoleLog(`✗ Failed to save: ${result.error}`, 'error');
      }
    } catch (error) {
      addConsoleLog(`✗ Error saving: ${error.message}`, 'error');
    } finally {
      setIsSavingWebsiteItems(false);
    }
  };

  const handleDiscardWebsitePreview = () => {
    setWebsitePreviewItems(null);
    setWebsiteSelectedItems({});
    addConsoleLog('Website preview discarded.', 'info');
  };

  // Photo upload handlers
  const pasteCountRef = useRef(0);

  const handlePaste = (e) => {
    if (!selectedRestaurant || isProcessingPhotos) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (blob) {
          pasteCountRef.current += 1;
          const ext = blob.type.split('/')[1] || 'png';
          const file = new File([blob], `pasted-image-${pasteCountRef.current}.${ext}`, { type: blob.type });
          imageFiles.push(file);
        }
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      setPendingFiles(prev => [...prev, ...imageFiles]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!selectedRestaurant || files.length === 0) return;

    // Add files to pending (not directly to queue)
    setPendingFiles(prev => [...prev, ...files]);

    // Reset file input
    e.target.value = '';
  };

  const handleAddToQueue = () => {
    if (!selectedRestaurant || pendingFiles.length === 0) return;

    const restaurant = restaurants.find(r => r.id === selectedRestaurant);
    if (!restaurant) return;

    // Check if restaurant already in queue
    const existingIndex = uploadQueue.findIndex(q => q.restaurantId === selectedRestaurant);
    if (existingIndex >= 0) {
      // Add files to existing queue entry
      setUploadQueue(prev => prev.map((q, i) =>
        i === existingIndex
          ? { ...q, files: [...q.files, ...pendingFiles] }
          : q
      ));
    } else {
      // Add new queue entry
      setUploadQueue(prev => [...prev, {
        restaurantId: selectedRestaurant,
        restaurantName: restaurant.Name,
        files: pendingFiles
      }]);
    }

    // Clear pending files and reset restaurant selection for next addition
    setPendingFiles([]);
    setSelectedRestaurant('');
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearPending = () => {
    setPendingFiles([]);
  };

  const handleRemoveFromQueue = (restaurantId, fileIndex = null) => {
    if (fileIndex !== null) {
      // Remove specific file
      setUploadQueue(prev => prev.map(q =>
        q.restaurantId === restaurantId
          ? { ...q, files: q.files.filter((_, i) => i !== fileIndex) }
          : q
      ).filter(q => q.files.length > 0));
    } else {
      // Remove entire restaurant from queue
      setUploadQueue(prev => prev.filter(q => q.restaurantId !== restaurantId));
    }
  };

  const handleClearQueue = () => {
    setUploadQueue([]);
    setPendingFiles([]);
    setSelectedRestaurant('');
    setExtractedData(null);
    setSelectedExtractions({});
    setExtractionTypeOverrides({});
  };

  const handleProcessPhotos = async () => {
    if (uploadQueue.length === 0) return;

    setIsProcessingPhotos(true);
    setExtractedData(null);
    setSelectedExtractions({});
    setExtractionTypeOverrides({});
    setConsoleOutput([]);
    setIsConsoleExpanded(true);
    setConsoleSource('upload');

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    try {
      addConsoleLog('Starting photo processing...', 'info');

      // Process each restaurant's photos
      const allResults = [];
      for (const queueItem of uploadQueue) {
        addConsoleLog(`Processing ${queueItem.files.length} photo(s) for ${queueItem.restaurantName}...`, 'info');

        const formData = new FormData();
        formData.append('restaurantId', queueItem.restaurantId);
        formData.append('restaurantName', queueItem.restaurantName);
        formData.append('baseId', activeBaseId);
        queueItem.files.forEach((file, i) => {
          formData.append('photos', file);
        });

        const response = await fetch(`${apiUrl}/api/process-photos`, {
          method: 'POST',
          headers: getAuthHeaders(null), // null content-type — browser sets multipart boundary
          body: formData
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success) {
          const data = result.data;

          // Log debug info if present
          if (data.debug) {
            addConsoleLog(`Files processed: ${data.debug.filesProcessed}`, 'info');
            if (data.debug.ocrTextExtracted) {
              addConsoleLog(`OCR extracted text: "${data.debug.ocrTextExtracted}"`, 'info');
            }
            if (data.debug.ocrErrors && data.debug.ocrErrors.length > 0) {
              data.debug.ocrErrors.forEach(err => addConsoleLog(`OCR: ${err}`, 'warning'));
            }
          }
          if (data.claudeDebug) {
            if (data.claudeDebug.success) {
              addConsoleLog(`Claude API: Success`, 'info');
              if (data.claudeDebug.rawResponse) {
                addConsoleLog(`Claude response: ${data.claudeDebug.rawResponse}`, 'info');
              }
              if (data.claudeDebug.parseError) {
                addConsoleLog(`JSON parse error: ${data.claudeDebug.parseError}`, 'error');
              }
            } else {
              addConsoleLog(`Claude API error: ${data.claudeDebug.error}`, 'error');
            }
          }

          // Count extracted items
          const hhCount = data.happyHours?.length || 0;
          const eventCount = data.events?.length || 0;
          const specialCount = data.specials?.length || 0;
          const totalItems = hhCount + eventCount + specialCount;

          if (totalItems > 0) {
            addConsoleLog(`✓ Extracted ${totalItems} items from ${queueItem.restaurantName} (${hhCount} happy hours, ${eventCount} events, ${specialCount} specials)`, 'success');
          } else {
            addConsoleLog(`⚠ No items extracted from ${queueItem.restaurantName}`, 'warning');
          }

          allResults.push({
            restaurantId: queueItem.restaurantId,
            restaurantName: queueItem.restaurantName,
            ...data
          });
        } else {
          addConsoleLog(`✗ Failed for ${queueItem.restaurantName}: ${result.error}`, 'error');
        }
      }

      setExtractedData(allResults);

      // Initialize all extractions as selected
      const initialSelections = {};
      allResults.forEach(result => {
        if (result.happyHours) {
          result.happyHours.forEach((_, i) => {
            initialSelections[`${result.restaurantId}-hh-${i}`] = true;
          });
        }
        if (result.events) {
          result.events.forEach((_, i) => {
            initialSelections[`${result.restaurantId}-event-${i}`] = true;
          });
        }
        if (result.specials) {
          result.specials.forEach((_, i) => {
            initialSelections[`${result.restaurantId}-special-${i}`] = true;
          });
        }
      });
      setSelectedExtractions(initialSelections);

      const totalExtracted = allResults.reduce((sum, r) =>
        sum + (r.happyHours?.length || 0) + (r.events?.length || 0) + (r.specials?.length || 0), 0);

      if (totalExtracted > 0) {
        addConsoleLog(`Processing complete. Found ${totalExtracted} items. Review extracted data below.`, 'success');
      } else {
        addConsoleLog(`Processing complete but no items were extracted. Check the debug info above.`, 'warning');
      }
    } catch (error) {
      addConsoleLog(`Error processing photos: ${error.message}`, 'error');
    } finally {
      setIsProcessingPhotos(false);
    }
  };

  const handleToggleExtraction = (key) => {
    setSelectedExtractions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveExtractions = async () => {
    if (!extractedData) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    setIsProcessingPhotos(true);
    addConsoleLog('Saving selected items to database...', 'info');

    try {
      // Build payload with only selected items, re-bucketing based on any
      // per-item type overrides the user set in the review UI.
      const payload = extractedData.map(result => {
        const buckets = { happyHours: [], events: [], specials: [] };
        const pushItem = (item, bucketKey, idx) => {
          const key = `${result.restaurantId}-${bucketKey}-${idx}`;
          if (!selectedExtractions[key]) return;
          const effType = extractionTypeOverrides[key] || bucketKeyToType(bucketKey);
          const targetBucket = BUCKET_BY_TYPE[effType];
          if (targetBucket) buckets[targetBucket].push(item);
        };
        (result.happyHours || []).forEach((item, i) => pushItem(item, 'hh', i));
        (result.events || []).forEach((item, i) => pushItem(item, 'event', i));
        (result.specials || []).forEach((item, i) => pushItem(item, 'special', i));
        return { restaurantId: result.restaurantId, ...buckets };
      }).filter(r => r.happyHours.length > 0 || r.events.length > 0 || r.specials.length > 0);

      const response = await fetch(`${apiUrl}/api/save-extractions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ extractions: payload, baseId: activeBaseId })
      });

      const result = await response.json();
      if (result.success) {
        addConsoleLog(`✓ Saved ${result.savedCount} items to database`, 'success');
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach(err => addConsoleLog(`⚠ ${err}`, 'warning'));
        }
        // Clear the queue and extracted data after successful save
        handleClearQueue();
      } else {
        addConsoleLog(`✗ Failed to save: ${result.error}`, 'error');
      }
    } catch (error) {
      addConsoleLog(`Error saving: ${error.message}`, 'error');
    } finally {
      setIsProcessingPhotos(false);
    }
  };

  // Text upload handlers
  const handleProcessText = async () => {
    if (!textRestaurant || !pastedText.trim()) return;

    const restaurant = restaurants.find(r => r.id === textRestaurant);
    if (!restaurant) return;

    setIsProcessingText(true);
    setTextExtractedData(null);
    setTextSelectedExtractions({});
    setTextExtractionTypeOverrides({});
    setConsoleOutput([]);
    setIsConsoleExpanded(true);
    setConsoleSource('upload');

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    try {
      addConsoleLog(`Processing text for ${restaurant.Name}...`, 'info');
      addConsoleLog(`Text length: ${pastedText.length} characters`, 'info');

      const response = await fetch(`${apiUrl}/api/process-text`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          restaurantId: textRestaurant,
          restaurantName: restaurant.Name,
          text: pastedText,
          baseId: activeBaseId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        const data = result.data;

        // Log debug info if present
        if (data.debug) {
          addConsoleLog(`Text processed: ${data.debug.textLength} chars`, 'info');
          if (data.debug.emojisRemoved > 0) {
            addConsoleLog(`Emojis removed: ${data.debug.emojisRemoved} characters`, 'info');
          }
        }
        if (data.claudeDebug) {
          if (data.claudeDebug.success) {
            addConsoleLog(`Claude API: Success`, 'info');
            if (data.claudeDebug.parseError) {
              addConsoleLog(`JSON parse error: ${data.claudeDebug.parseError}`, 'error');
            }
          } else {
            addConsoleLog(`Claude API error: ${data.claudeDebug.error}`, 'error');
          }
        }

        // Count extracted items
        const hhCount = data.happyHours?.length || 0;
        const eventCount = data.events?.length || 0;
        const specialCount = data.specials?.length || 0;
        const totalItems = hhCount + eventCount + specialCount;

        if (totalItems > 0) {
          addConsoleLog(`Extracted ${totalItems} items from ${restaurant.Name} (${hhCount} happy hours, ${eventCount} events, ${specialCount} specials)`, 'success');
        } else {
          addConsoleLog(`No items extracted from the text`, 'warning');
        }

        const allResults = [{
          restaurantId: textRestaurant,
          restaurantName: restaurant.Name,
          ...data
        }];

        setTextExtractedData(allResults);

        // Initialize all extractions as selected
        const initialSelections = {};
        allResults.forEach(r => {
          if (r.happyHours) {
            r.happyHours.forEach((_, i) => {
              initialSelections[`${r.restaurantId}-hh-${i}`] = true;
            });
          }
          if (r.events) {
            r.events.forEach((_, i) => {
              initialSelections[`${r.restaurantId}-event-${i}`] = true;
            });
          }
          if (r.specials) {
            r.specials.forEach((_, i) => {
              initialSelections[`${r.restaurantId}-special-${i}`] = true;
            });
          }
        });
        setTextSelectedExtractions(initialSelections);

        if (totalItems > 0) {
          addConsoleLog(`Processing complete. Found ${totalItems} items. Review extracted data below.`, 'success');
        } else {
          addConsoleLog(`Processing complete but no items were extracted. The text may not contain recognizable happy hour, event, or special information.`, 'warning');
        }
      } else {
        addConsoleLog(`Failed: ${result.error}`, 'error');
      }
    } catch (error) {
      addConsoleLog(`Error processing text: ${error.message}`, 'error');
    } finally {
      setIsProcessingText(false);
    }
  };

  const handleTextToggleExtraction = (key) => {
    setTextSelectedExtractions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveTextExtractions = async () => {
    if (!textExtractedData) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    setIsProcessingText(true);
    addConsoleLog('Saving selected items to database...', 'info');

    try {
      // Build payload with only selected items, re-bucketing based on any
      // per-item type overrides the user set in the review UI.
      const payload = textExtractedData.map(result => {
        const buckets = { happyHours: [], events: [], specials: [] };
        const pushItem = (item, bucketKey, idx) => {
          const key = `${result.restaurantId}-${bucketKey}-${idx}`;
          if (!textSelectedExtractions[key]) return;
          const effType = textExtractionTypeOverrides[key] || bucketKeyToType(bucketKey);
          const targetBucket = BUCKET_BY_TYPE[effType];
          if (targetBucket) buckets[targetBucket].push(item);
        };
        (result.happyHours || []).forEach((item, i) => pushItem(item, 'hh', i));
        (result.events || []).forEach((item, i) => pushItem(item, 'event', i));
        (result.specials || []).forEach((item, i) => pushItem(item, 'special', i));
        return { restaurantId: result.restaurantId, source: 'social://text-upload', ...buckets };
      }).filter(r => r.happyHours.length > 0 || r.events.length > 0 || r.specials.length > 0);

      const response = await fetch(`${apiUrl}/api/save-extractions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ extractions: payload, baseId: activeBaseId })
      });

      const result = await response.json();
      if (result.success) {
        addConsoleLog(`Saved ${result.savedCount} items to database`, 'success');
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach(err => addConsoleLog(`⚠ ${err}`, 'warning'));
        }
        // Clear after successful save
        handleClearTextUpload();
      } else {
        addConsoleLog(`Failed to save: ${result.error}`, 'error');
      }
    } catch (error) {
      addConsoleLog(`Error saving: ${error.message}`, 'error');
    } finally {
      setIsProcessingText(false);
    }
  };

  const handleClearTextUpload = () => {
    setTextRestaurant('');
    setPastedText('');
    setTextExtractedData(null);
    setTextSelectedExtractions({});
    setTextExtractionTypeOverrides({});
  };

  const handleLogout = async () => {
    if (useNewAuth) {
      await authLogout();
    } else {
      clearSession();
    }
    navigate('/login');
  };

  const copyErrorLog = () => {
    if (scrapeResult?.errorLog) {
      navigator.clipboard.writeText(scrapeResult.errorLog);
      alert('Error log copied to clipboard!');
    }
  };

  const downloadErrorLog = () => {
    if (scrapeResult?.errorLog) {
      const blob = new Blob([scrapeResult.errorLog], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scraper-errors-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // ── Tools & Settings handlers ──

  const addToolConsoleLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    let cleanMessage = message;
    // Strip Python logging prefixes
    const prefixes = ['INFO:__main__:', 'WARNING:__main__:', 'ERROR:__main__:', 'DEBUG:__main__:'];
    for (const prefix of prefixes) {
      if (cleanMessage.startsWith(prefix)) {
        cleanMessage = cleanMessage.substring(prefix.length);
        break;
      }
    }
    if (!cleanMessage.trim() || cleanMessage.match(/^[=]+$/)) return;
    setToolConsoleOutput(prev => [...prev, { timestamp, message: cleanMessage, type }]);
  }, []);

  const handleRunTool = async (toolType) => {
    setIsRunningTool(true);
    setRunningToolType(toolType);
    setLastToolType(toolType);
    setToolConsoleOutput([]);
    setIsToolConsoleExpanded(true);

    // Auto-scroll the console into view after a short delay (for rendering)
    setTimeout(() => {
      const isSpecialScraper = toolType === 'scrape-pollyanna' || toolType === 'scrape-stc-alliance';
      const consoleEl = isSpecialScraper ? specialScraperConsoleRef.current : toolConsoleRef.current;
      if (consoleEl) {
        consoleEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const endpointMap = {
      'validate-data': '/api/validate-data',
      'validate-filters': '/api/validate-filters',
      'cleanup-events': '/api/cleanup-events',
      'audit-descriptions': '/api/audit-descriptions',
      'scrape-pollyanna': '/api/scrape-pollyanna',
      'scrape-stc-alliance': '/api/scrape-stc-alliance',
    };
    const endpoint = endpointMap[toolType] || '/api/validate-data';

    // Build request body - include dryRun for cleanup and special scrapers
    const requestBody = { verbose: toolVerbose, baseId: activeBaseId };
    if (toolType === 'cleanup-events' || toolType === 'scrape-pollyanna' || toolType === 'scrape-stc-alliance') {
      requestBody.dryRun = cleanupDryRun;
    }

    try {
      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      if (!response.body) throw new Error('Response body is null');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = ''; // Buffer for reassembling SSE messages split across chunks

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        sseBuffer += chunk;
        const messages = sseBuffer.split('\n\n');
        sseBuffer = messages.pop(); // Last segment may be incomplete
        for (const line of messages) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.type === 'complete') {
                addToolConsoleLog(data.message, data.hasErrors ? 'error' : 'success');
              } else {
                addToolConsoleLog(data.message, data.type);
              }
            } catch (e) { /* skip parse errors */ }
          }
        }
      }
    } catch (error) {
      addToolConsoleLog(`Error: ${error.message}`, 'error');
    } finally {
      setIsRunningTool(false);
      setRunningToolType(null);
    }
  };

  const copyToolConsole = () => {
    const text = toolConsoleOutput.map(l => `[${l.timestamp}] ${l.message}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      // Brief visual feedback could be added here
    });
  };

  const downloadToolConsole = () => {
    const text = toolConsoleOutput.map(l => `[${l.timestamp}] ${l.message}`).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tool-output-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearToolConsole = () => {
    setToolConsoleOutput([]);
  };

  const handleSettingChange = async (record, field, value) => {
    const recordId = record.id;
    setSettingsSaving(prev => ({ ...prev, [recordId]: true }));
    try {
      await updateSettingForBase(activeBaseId, recordId, { [field]: value });
      setSettings(prev => prev.map(s =>
        s.id === recordId
          ? { ...s, fields: { ...s.fields, [field]: value } }
          : s
      ));
    } catch (error) {
      console.error('Failed to update setting:', error);
      alert(`Failed to update setting: ${error.message}`);
    } finally {
      setSettingsSaving(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const handleAddKeyword = (type) => {
    const raw = keywordInput[type].trim().toLowerCase();
    if (!raw) return;
    const baseList = type === 'food' ? FOOD_KEYWORDS : DRINK_KEYWORDS;
    const customList = type === 'food' ? customFoodKeywords : customDrinkKeywords;
    if (baseList.includes(raw) || customList.includes(raw)) return;
    const updated = [...customList, raw];
    if (type === 'food') setCustomFoodKeywords(updated);
    else setCustomDrinkKeywords(updated);
    setKeywordInput(prev => ({ ...prev, [type]: '' }));
    setCustomKeywords(
      type === 'food' ? updated : customFoodKeywords,
      type === 'drink' ? updated : customDrinkKeywords
    );
  };

  const handleRemoveKeyword = (type, kw) => {
    const updated = (type === 'food' ? customFoodKeywords : customDrinkKeywords).filter(k => k !== kw);
    if (type === 'food') setCustomFoodKeywords(updated);
    else setCustomDrinkKeywords(updated);
    setCustomKeywords(
      type === 'food' ? updated : customFoodKeywords,
      type === 'drink' ? updated : customDrinkKeywords
    );
  };

  const handleSaveKeywords = async () => {
    setKeywordSaving(true);
    try {
      const foodResult = await saveCustomKeywordsForBase(activeBaseId, 'food', customFoodKeywords, foodKeywordRecordId);
      const drinkResult = await saveCustomKeywordsForBase(activeBaseId, 'drink', customDrinkKeywords, drinkKeywordRecordId);
      if (!foodKeywordRecordId && foodResult?.id) setFoodKeywordRecordId(foodResult.id);
      if (!drinkKeywordRecordId && drinkResult?.id) setDrinkKeywordRecordId(drinkResult.id);
      // Invalidate cache so next load reads fresh data
      clearAirtableCache();
      alert('Keywords saved!');
    } catch (error) {
      alert(`Failed to save keywords: ${error.message}`);
    } finally {
      setKeywordSaving(false);
    }
  };

  const renderSettingRow = (settingName, displayLabel, field, inputType, tooltip) => {
    const record = settings.find(s => {
      const name = s.fields['Setting Name']?.toLowerCase().trim();
      return name === settingName.toLowerCase();
    });

    if (!record) return (
      <div className="setting-row" key={settingName}>
        <div className="setting-label-group">
          <span className="setting-label">{displayLabel}</span>
          {tooltip && (
            <span className="setting-info-btn" tabIndex={0}>
              ℹ️
              <span className="setting-tooltip">{tooltip}</span>
            </span>
          )}
        </div>
        <span className="setting-missing">Not configured</span>
      </div>
    );

    const isSaving = settingsSaving[record.id];
    const currentValue = field === 'Enabled' ? (record.fields.Enabled ?? false) : (record.fields.Value ?? 0);
    const editValue = settingsEdits[record.id];

    return (
      <div className="setting-row" key={settingName}>
        <div className="setting-label-group">
          <span className="setting-label">{displayLabel}</span>
          {tooltip && (
            <span className="setting-info-btn" tabIndex={0}>
              ℹ️
              <span className="setting-tooltip">{tooltip}</span>
            </span>
          )}
        </div>
        <div className="setting-control">
          {inputType === 'toggle' ? (
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={currentValue}
                onChange={(e) => handleSettingChange(record, field, e.target.checked)}
                disabled={isSaving}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-text">{currentValue ? 'ON' : 'OFF'}</span>
            </label>
          ) : (
            <input
              type="number"
              className="setting-number-input"
              value={editValue !== undefined ? editValue : currentValue}
              onChange={(e) => setSettingsEdits(prev => ({ ...prev, [record.id]: e.target.value }))}
              onBlur={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val) && val !== currentValue) {
                  handleSettingChange(record, field, val);
                }
                setSettingsEdits(prev => {
                  const next = { ...prev };
                  delete next[record.id];
                  return next;
                });
              }}
              disabled={isSaving}
              step="any"
            />
          )}
          {isSaving && <span className="setting-saving">Saving...</span>}
        </div>
      </div>
    );
  };

  // ── Content Review handlers ──

  const fetchReviewSubmissions = useCallback(async () => {
    if (!useNewAuth || !isAdmin) return;
    setReviewLoading(true);
    setReviewError(null);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const params = new URLSearchParams({ baseId: activeBaseId });
      if (reviewFilter && reviewFilter !== 'all') params.set('status', reviewFilter);
      const res = await fetch(`${apiUrl}/api/admin/review?${params.toString()}`, {
        headers: getAuthHeaders(null),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setReviewSubmissions(data.submissions || []);
    } catch (err) {
      setReviewError(err.message);
    } finally {
      setReviewLoading(false);
    }
  }, [useNewAuth, isAdmin, activeBaseId, reviewFilter, getAuthHeaders]);

  const fetchReviewPendingCount = useCallback(async () => {
    if (!useNewAuth || !isAdmin) return;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const res = await fetch(`${apiUrl}/api/admin/review/count?baseId=${activeBaseId}`, {
        headers: getAuthHeaders(null),
      });
      if (res.ok) {
        const data = await res.json();
        setReviewPendingCount(data.count || 0);
      }
    } catch (err) {
      // Silent fail for count
    }
  }, [useNewAuth, isAdmin, activeBaseId, getAuthHeaders]);

  useEffect(() => {
    if (isContentReviewExpanded && useNewAuth && isAdmin) {
      fetchReviewSubmissions();
    }
  }, [isContentReviewExpanded, fetchReviewSubmissions, useNewAuth, isAdmin]);

  // Fetch pending count on mount and when market changes
  useEffect(() => {
    if (useNewAuth && isAdmin) {
      fetchReviewPendingCount();
    }
  }, [fetchReviewPendingCount, useNewAuth, isAdmin]);

  const handleReviewAction = async (submissionId, action) => {
    if ((action === 'reject' || action === 'revision') && !reviewNotes.trim()) {
      alert('Please enter review notes before rejecting or requesting revision.');
      return;
    }
    setReviewActionLoading(submissionId);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const res = await fetch(`${apiUrl}/api/admin/review/${submissionId}/${action}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes: reviewNotes, baseId: activeBaseId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      // Show success message
      const result = await res.json().catch(() => ({}));
      const actionLabels = { approve: 'approved & published', reject: 'rejected', revision: 'sent back for revision' };
      const publishedCount = result.published?.savedCount || 0;
      const msg = action === 'approve' && publishedCount > 0
        ? `Content ${actionLabels[action]}! ${publishedCount} item${publishedCount !== 1 ? 's' : ''} saved to database.`
        : `Content ${actionLabels[action] || action}.`;
      setReviewError(null);
      setReviewNotes('');
      setExpandedSubmission(null);
      await fetchReviewSubmissions();
      await fetchReviewPendingCount();
      alert(msg);
    } catch (err) {
      alert(`Review action failed: ${err.message}`);
    } finally {
      setReviewActionLoading(null);
    }
  };

  // ── Audit Log handlers ──

  const fetchAuditLogs = useCallback(async (loadMore = false) => {
    if (!useNewAuth || !isAdmin) return;
    setAuditLoading(true);
    setAuditError(null);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const params = new URLSearchParams();
      if (auditFilterAction) params.set('action', auditFilterAction);
      if (isSuperAdmin && auditFilterMarket) params.set('market', auditFilterMarket);
      if (auditDateStart) params.set('startDate', new Date(auditDateStart).toISOString());
      if (auditDateEnd) params.set('endDate', new Date(auditDateEnd + 'T23:59:59').toISOString());
      params.set('pageSize', '25');
      if (loadMore && auditOffset) params.set('offset', auditOffset);

      const res = await fetch(`${apiUrl}/api/admin/audit?${params.toString()}`, {
        headers: getAuthHeaders(null),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (loadMore) {
        setAuditLogs(prev => [...prev, ...(data.records || [])]);
      } else {
        setAuditLogs(data.records || []);
      }
      setAuditOffset(data.offset || null);
      setAuditHasMore(!!data.offset);
    } catch (err) {
      setAuditError(err.message);
    } finally {
      setAuditLoading(false);
    }
  }, [useNewAuth, isAdmin, isSuperAdmin, auditFilterAction, auditFilterMarket, auditDateStart, auditDateEnd, auditOffset, getAuthHeaders]);

  useEffect(() => {
    if (isAuditLogExpanded && useNewAuth && isAdmin) {
      setAuditOffset(null);
      fetchAuditLogs(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuditLogExpanded, useNewAuth, isAdmin]);

  const handleAuditFilterApply = () => {
    setAuditOffset(null);
    fetchAuditLogs(false);
  };

  // ── Venue Tier Management handlers ──

  const fetchVtVenues = useCallback(async () => {
    if (!useNewAuth || !isAdmin) return;
    setVtLoading(true);
    setVtError(null);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const res = await fetch(`${apiUrl}/api/my-venues?baseId=${activeBaseId}`, {
        headers: getAuthHeaders(null),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const sorted = (data.venues || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setVtVenues(sorted);
      setVtEdits({});
    } catch (err) {
      setVtError(err.message);
    } finally {
      setVtLoading(false);
    }
  }, [useNewAuth, isAdmin, activeBaseId, getAuthHeaders]);

  useEffect(() => {
    if (isVenueTierExpanded && useNewAuth && isAdmin) {
      fetchVtVenues();
    }
  }, [isVenueTierExpanded, fetchVtVenues, useNewAuth, isAdmin]);

  const handleVtEdit = (venueId, field, value) => {
    setVtEdits(prev => ({
      ...prev,
      [venueId]: {
        ...prev[venueId],
        [field]: value,
      },
    }));
  };

  const handleVtSave = async (venue) => {
    const edits = vtEdits[venue.id];
    if (!edits || Object.keys(edits).length === 0) return;

    setVtSaving(venue.id);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const res = await fetch(`${apiUrl}/api/venues/${venue.id}/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ baseId: activeBaseId, ...edits }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      // Update local state with saved values
      setVtVenues(prev => prev.map(v => v.id === venue.id ? { ...v, ...edits } : v));
      setVtEdits(prev => {
        const next = { ...prev };
        delete next[venue.id];
        return next;
      });
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setVtSaving(null);
    }
  };

  // ── User Management handlers ──

  const fetchUsers = useCallback(async () => {
    if (!useNewAuth) return;
    setUserMgmtLoading(true);
    setUserMgmtError(null);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const params = new URLSearchParams();
      if (userMgmtFilterRole) params.set('role', userMgmtFilterRole);
      if (userMgmtFilterStatus) params.set('status', userMgmtFilterStatus);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${apiUrl}/api/admin/users${qs}`, {
        headers: getAuthHeaders(null),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setUserMgmtUsers(data.users || []);
    } catch (err) {
      setUserMgmtError(err.message);
    } finally {
      setUserMgmtLoading(false);
    }
  }, [useNewAuth, userMgmtFilterRole, userMgmtFilterStatus, getAuthHeaders]);

  // Fetch users on mount (for badge count) and when the section is expanded
  useEffect(() => {
    if (useNewAuth && isAdmin) {
      fetchUsers();
    }
  }, [isUserMgmtExpanded, useNewAuth, isAdmin, fetchUsers]);

  const resetUserForm = () => {
    setUmFormName('');
    setUmFormEmail('');
    setUmFormPassword('');
    setUmFormRole('venue_owner');
    setUmFormMarkets([]);
    setUmFormTier('free');
    setUmFormVenues('');
    setUmFormNotes('');
    setUmFormStatus('active');
    setUmFormError('');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUmFormError('');
    setUmFormSaving(true);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const body = {
        name: umFormName.trim(),
        email: umFormEmail.trim(),
        password: umFormPassword,
        role: umFormRole,
        markets: umFormMarkets,
        accountTier: umFormTier,
      };
      if (umFormVenues.trim()) {
        body.venues = umFormVenues.split(',').map(v => v.trim()).filter(Boolean);
      }
      if (umFormNotes.trim()) body.notes = umFormNotes.trim();

      const res = await fetch(`${apiUrl}/api/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create user');
      // Success — go back to list and refresh
      setUserMgmtView('list');
      resetUserForm();
      fetchUsers();
    } catch (err) {
      setUmFormError(err.message);
    } finally {
      setUmFormSaving(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!userMgmtEditTarget) return;
    setUmFormError('');
    setUmFormSaving(true);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const updates = {};
      if (umFormName.trim()) updates.name = umFormName.trim();
      if (umFormRole) updates.role = umFormRole;
      if (umFormMarkets.length > 0) updates.markets = umFormMarkets;
      if (umFormTier) updates.accountTier = umFormTier;
      if (umFormStatus) updates.status = umFormStatus;
      if (umFormVenues.trim()) {
        updates.venues = umFormVenues.split(',').map(v => v.trim()).filter(Boolean);
      }
      if (umFormNotes.trim()) updates.notes = umFormNotes.trim();
      // Only send password if changed
      if (umFormPassword) updates.password = umFormPassword;

      const res = await fetch(`${apiUrl}/api/admin/users/${userMgmtEditTarget.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update user');
      setUserMgmtView('list');
      setUserMgmtEditTarget(null);
      resetUserForm();
      fetchUsers();
    } catch (err) {
      setUmFormError(err.message);
    } finally {
      setUmFormSaving(false);
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userName}? They will be logged out immediately.`)) return;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const res = await fetch(`${apiUrl}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(null),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to deactivate user');
      fetchUsers();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const startEditUser = (user) => {
    setUserMgmtEditTarget(user);
    setUmFormName(user.name || '');
    setUmFormEmail(user.email || '');
    setUmFormPassword('');
    setUmFormRole(user.role || 'venue_owner');
    setUmFormMarkets(user.markets || []);
    setUmFormTier(user.account_tier || 'free');
    setUmFormVenues((user.venues || []).join(', '));
    setUmFormNotes(user.notes || '');
    setUmFormStatus(user.status || 'active');
    setUmFormError('');
    setUserMgmtView('edit');
  };

  const handleMarketToggle = (marketId) => {
    setUmFormMarkets(prev =>
      prev.includes(marketId) ? prev.filter(m => m !== marketId) : [...prev, marketId]
    );
  };

  const isAnyProcessRunning = isScraping || isScrapingAggregator || isProcessingPhotos || isProcessingText || isRunningTool || isSavingAggregatorEvents || isSavingWebsiteItems;

  // Extract initials for avatar (e.g., "Kevin Howe" -> "KH", "admin@test.com" -> "A")
  const getInitials = () => {
    if (!authUser) return '?';
    const name = authUser.name || authUser.email || '';
    if (name.includes(' ')) {
      const parts = name.split(' ');
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0]?.toUpperCase() || '?';
  };

  return (
    <div className="admin-console">
      <SEOHead title="Admin Console" noindex market={market} />
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-left">
            <h1>{isVenueOwner ? 'Venue Dashboard' : 'Admin Console'}</h1>
            <p>
              {useNewAuth && authUser ? (
                <>
                  {authUser.name || authUser.email}
                  <span style={{ opacity: 0.7, marginLeft: '8px', fontSize: '0.85em' }}>
                    {userRole === 'super_admin' ? 'Super Admin' :
                     userRole === 'admin' ? 'Admin' : 'Venue Owner'}
                  </span>
                </>
              ) : (
                'Manage data collection and database'
              )}
            </p>
            {/* Market Switcher — Super Admin only */}
            {isSuperAdmin && (
              <div className="market-switcher" style={{ marginTop: '0.75rem' }}>
                <label className="market-switcher-label">Market:</label>
                <select
                  className="market-switcher-select"
                  value={adminMarketId}
                  onChange={(e) => {
                    setAdminMarketId(e.target.value);
                    // Reset to websites tab if switching away from TCC while on special scrapers tab
                    if (e.target.value !== 'tcc' && activeScraperTab === 'special') {
                      setActiveScraperTab('websites');
                    }
                  }}
                >
                  {Object.values(allMarkets).filter(m => typeof m === 'object' && m.id).map(m => (
                    <option key={m.id} value={m.id}>
                      {m.shortName} — {m.cities?.[0] || m.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {market.dashboardLogo && (
            <div className="admin-header-center">
              <img src={market.dashboardLogo} alt={market.logoAlt || 'Logo'} className="admin-header-logo" />
            </div>
          )}
          <div className="admin-header-actions">
            <MarketLink to="/" className="admin-ghost-button">
              ← Site
            </MarketLink>
            {isSuperAdmin && (
              <MarketLink to="/admin/dashboard" className="admin-pill-button">
                Dashboard
              </MarketLink>
            )}
            {useNewAuth && authUser ? (
              <div className="admin-avatar-wrapper" ref={avatarDropdownRef}>
                <button
                  className="admin-avatar"
                  onClick={() => setAvatarDropdownOpen(prev => !prev)}
                  aria-label="User menu"
                  aria-expanded={avatarDropdownOpen}
                >
                  {getInitials()}
                </button>
                {avatarDropdownOpen && (
                  <div className="admin-avatar-dropdown">
                    <div className="admin-avatar-dropdown-header">
                      <div className="admin-avatar-dropdown-name">
                        {authUser.name || authUser.email}
                      </div>
                      <div className="admin-avatar-dropdown-role">
                        {userRole === 'super_admin' ? 'Super Admin' :
                         userRole === 'admin' ? 'Admin' : 'Venue Owner'}
                      </div>
                    </div>
                    <div className="admin-avatar-dropdown-divider" />
                    <button
                      className="admin-avatar-dropdown-item admin-avatar-dropdown-logout"
                      onClick={handleLogout}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={handleLogout} className="admin-ghost-button admin-ghost-button--danger">
                Logout
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="admin-container">
        {/* Market Banner */}
        <div className="admin-market-banner">
          <h2 className="admin-market-banner-title">
            {activeAdminMarket.shortName} — {activeAdminMarket.name}
          </h2>
          {activeAdminMarket.regionLabel && (
            <p className="admin-market-banner-region">{activeAdminMarket.regionLabel}</p>
          )}
        </div>

        {/* Database Metrics Section - Admin/Super Admin only */}
        {isAdmin && (
        <section className="admin-section">
          <h2>📊 Database Status</h2>
          <p className="section-description">
            Current counts of restaurants and content displayed on the website.
          </p>

          {metricsLoading && (
            <div className="metrics-loading">
              <div className="spinner"></div>
              <p>Loading metrics...</p>
            </div>
          )}

          {metricsError && (
            <div className="metrics-error">
              <p>Failed to load metrics: {metricsError}</p>
              <button
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          )}

          {dbMetrics && !metricsLoading && (
            <>
              {/* Totals Row */}
              <div className="db-metrics-grid">
                <div className="db-metric-card restaurants">
                  <div className="db-metric-icon">🍽️</div>
                  <div className="db-metric-content">
                    <span className="db-metric-value">{dbMetrics.activeRestaurants}</span>
                    <span className="db-metric-label">Active Restaurants</span>
                    <span className="db-metric-sub">of {dbMetrics.totalRestaurants} total</span>
                  </div>
                </div>

                <div className="db-metric-card happy-hours">
                  <div className="db-metric-icon">🍺</div>
                  <div className="db-metric-content">
                    <span className="db-metric-value">{dbMetrics.activeHappyHours}</span>
                    <span className="db-metric-label">Happy Hour Deals</span>
                    <span className="db-metric-sub">currently active</span>
                  </div>
                </div>

                <div className="db-metric-card specials">
                  <div className="db-metric-icon">⭐</div>
                  <div className="db-metric-content">
                    <span className="db-metric-value">{dbMetrics.activeSpecials}</span>
                    <span className="db-metric-label">Daily Specials</span>
                    <span className="db-metric-sub">currently active</span>
                  </div>
                </div>

                <div className="db-metric-card events">
                  <div className="db-metric-icon">🎉</div>
                  <div className="db-metric-content">
                    <span className="db-metric-value">{dbMetrics.activeEvents}</span>
                    <span className="db-metric-label">Events</span>
                    <span className="db-metric-sub">currently active</span>
                  </div>
                </div>
              </div>

              {/* Pie Charts */}
              <div className="pie-charts-container">
                {/* Row 1: Distribution */}
                <div className="pie-chart-row-label">Distribution</div>
                <div className="pie-charts-row">
                  <PieChart
                    title="Venues by City"
                    data={dbMetrics.cityCounts || {}}
                    colors={['#667eea', '#f59e0b', '#10b981', '#8b5cf6', '#9ca3af']}
                    footnote="*Goal: Keep balanced"
                  />
                  <PieChart
                    title="Venues by Type"
                    data={dbMetrics.typeCounts || {}}
                    colors={['#667eea', '#f59e0b', '#10b981', '#8b5cf6', '#f97316', '#ec4899', '#9ca3af']}
                  />
                  <PieChart
                    title="Events by Type"
                    data={dbMetrics.eventTypeCounts || {}}
                    colors={['#667eea', '#f59e0b', '#10b981', '#8b5cf6', '#f97316', '#ec4899', '#14b8a6', '#9ca3af']}
                  />
                </div>

                {/* Row 2: Data Health */}
                <div className="pie-chart-row-label">Data Health</div>
                <div className="pie-charts-row">
                  <PieChart
                    title="Content Coverage"
                    data={dbMetrics.coverageCounts ? {
                      'Complete (3/3)': dbMetrics.coverageCounts.complete,
                      'Partial (2/3)': dbMetrics.coverageCounts.partial,
                      'Minimal (1/3)': dbMetrics.coverageCounts.minimal,
                      'Empty (0/3)': dbMetrics.coverageCounts.empty
                    } : {}}
                    colors={['#10b981', '#667eea', '#f59e0b', '#ef4444']}
                  />
                  <PieChart
                    title="Data Freshness"
                    data={dbMetrics.freshnessCounts ? {
                      'Fresh (<7d)': dbMetrics.freshnessCounts.fresh,
                      '1-2 weeks': dbMetrics.freshnessCounts.week,
                      '2-4 weeks': dbMetrics.freshnessCounts.month,
                      'Stale (30d+)': dbMetrics.freshnessCounts.stale
                    } : {}}
                    colors={['#10b981', '#667eea', '#f59e0b', '#ef4444']}
                  />
                  <PieChart
                    title="Scraper Sources"
                    data={{ 'Coming Soon': 1 }}
                    colors={['#9ca3af']}
                    placeholder={true}
                  />
                </div>
              </div>

              {/* Stale Content Alert */}
              {dbMetrics.staleVenues && dbMetrics.staleVenues.length > 0 && (
                <StaleVenuesList
                  venues={dbMetrics.staleVenues}
                  thresholds={dbMetrics.staleThresholds}
                />
              )}
            </>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* SITE ANALYTICS SECTION - Collapsible (Admin/Super Admin only)     */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className={`collapsible-section-header ${isAnalyticsExpanded ? 'expanded' : ''}`}
            onClick={() => setIsAnalyticsExpanded(!isAnalyticsExpanded)}
          >
            <div className="collapsible-section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Site traffic and engagement analytics from Google Analytics. Shows how users interact with your market's listings.">i</span> 📈 Site Analytics</h2>
              <span className="section-description">Traffic, engagement, and venue leaderboard</span>
            </div>
            <button className="collapse-button">{isAnalyticsExpanded ? '▼' : '▶'}</button>
          </div>

          {isAnalyticsExpanded && (
            <div className="collapsible-section-content">
              {/* Period selector */}
              <div className="an-period-selector">
                {['7d', '30d', '90d'].map(p => (
                  <button
                    key={p}
                    className={`an-period-btn ${acAnalyticsPeriod === p ? 'an-period-btn--active' : ''}`}
                    onClick={() => setAcAnalyticsPeriod(p)}
                  >
                    {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                  </button>
                ))}
              </div>

              {acAnalyticsError ? (
                <div className="metrics-error">
                  <p>Analytics not available. Ensure GA4 credentials are configured on the server.</p>
                </div>
              ) : (
                <>
                  {/* Stat cards */}
                  <div className="an-stat-grid">
                    <StatCard
                      label="Page Views"
                      value={acTraffic ? acTraffic.totals.pageViews.toLocaleString() : null}
                      icon="👁️"
                      color="#667eea"
                      loading={acTrafficLoading}
                    />
                    <StatCard
                      label="Active Users"
                      value={acTraffic ? acTraffic.totals.activeUsers.toLocaleString() : null}
                      icon="👤"
                      color="#10b981"
                      loading={acTrafficLoading}
                    />
                    <StatCard
                      label="Sessions"
                      value={acTraffic ? acTraffic.totals.sessions.toLocaleString() : null}
                      icon="📊"
                      color="#f59e0b"
                      loading={acTrafficLoading}
                    />
                  </div>

                  {/* Daily traffic chart */}
                  <AnalyticsLineChart
                    title="Daily Traffic"
                    data={acTraffic?.daily || []}
                    lines={[
                      { key: 'pageViews', label: 'Page Views', color: '#667eea' },
                      { key: 'activeUsers', label: 'Active Users', color: '#10b981' },
                    ]}
                    loading={acTrafficLoading}
                    height={260}
                  />

                  {/* Venue leaderboard */}
                  <LeaderboardTable
                    title="Top Venues by Engagement"
                    data={acTopVenues?.venues || []}
                    loading={acTopVenuesLoading}
                    emptyText="Venue engagement data will appear as users interact with listings"
                  />
                </>
              )}
            </div>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* VENUE EDITOR SECTION - Collapsible (Admin/Super Admin only)    */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className={`collapsible-section-header ${isVenueEditorExpanded ? 'expanded' : ''}`}
            onClick={() => setIsVenueEditorExpanded(!isVenueEditorExpanded)}
          >
            <div className="collapsible-section-header-left">
              <h2>🏪 Venue Editor</h2>
              <span className="section-description">Browse and directly edit venues, deals, events, and specials</span>
            </div>
            <button className="collapse-button">{isVenueEditorExpanded ? '▼' : '▶'}</button>
          </div>

          {isVenueEditorExpanded && (
            <div style={{ padding: '16px' }}>
              <VenueEditor baseId={activeBaseId} getAuthHeaders={getAuthHeaders} />
            </div>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* SCRAPER MANAGEMENT SECTION - Collapsible (Admin/Super Admin only) */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className={`collapsible-section-header ${isScraperSectionExpanded ? 'expanded' : ''}`}
            onClick={() => setIsScraperSectionExpanded(!isScraperSectionExpanded)}
          >
            <div className="collapsible-section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Manage website scrapers, aggregator sources, and special scrapers. Run scrapes, toggle sources, and monitor extraction results.">i</span> 🤖 Scraper Management</h2>
              <span className="section-description">Website scrapers, aggregators, and special scrapers</span>
            </div>
            <button className="collapse-button">{isScraperSectionExpanded ? '▼' : '▶'}</button>
          </div>

          {isScraperSectionExpanded && (
            <div className="collapsible-section-content">
              {/* Tab Navigation for Scrapers */}
              <div className="tab-navigation">
                <button
                  className={`tab-button ${activeScraperTab === 'websites' ? 'active' : ''}`}
                  onClick={() => setActiveScraperTab('websites')}
                >
                  🌐 Website Scrapers
                </button>
                <button
                  className={`tab-button ${activeScraperTab === 'aggregators' ? 'active' : ''}`}
                  onClick={() => setActiveScraperTab('aggregators')}
                >
                  📋 Aggregators
                </button>
                {activeAdminMarket.id === 'tcc' && (
                <button
                  className={`tab-button ${activeScraperTab === 'special' ? 'active' : ''}`}
                  onClick={() => setActiveScraperTab('special')}
                >
                  ⭐ Special Scrapers
                </button>
                )}
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Website Scrapers Tab */}
                {activeScraperTab === 'websites' && (
              <div className="tab-panel">
                <p className="tab-description">
                  Scrape happy hours, specials, and events directly from restaurant websites.
                  The system visits each venue's website and extracts updated information.
                </p>

                <div className="scrape-controls">
                  <div className="scrape-controls-row">
                    <div className="form-group" style={{maxWidth: '300px'}}>
                      <label htmlFor="scrape-scope">Scrape Scope:</label>
                      <select
                        id="scrape-scope"
                        value={scrapeScope}
                        onChange={(e) => setScrapeScope(e.target.value)}
                        disabled={isScraping}
                      >
                        <option value="all">All</option>
                        <option value="happy_hour">Happy Hour Only</option>
                        <option value="specials">Restaurant Specials Only</option>
                        <option value="events">Events Only</option>
                      </select>
                    </div>

                    <div className="scrape-options">
                      <label className="checkbox-option" title="Bypass the extraction cache and force a fresh Claude API call, even if the page content hasn't changed since the last scrape.">
                        <input
                          type="checkbox"
                          checked={scrapeNoCache}
                          onChange={(e) => setScrapeNoCache(e.target.checked)}
                          disabled={isScraping}
                        />
                        <span>Force re-extract (skip cache)</span>
                      </label>
                      <label className="checkbox-option" title="Download images from the page and send them to Claude Vision alongside the text. Useful when key info like times or prices is embedded in images rather than HTML text.">
                        <input
                          type="checkbox"
                          checked={scrapeUseVision}
                          onChange={(e) => setScrapeUseVision(e.target.checked)}
                          disabled={isScraping}
                        />
                        <span>Use Vision (read text in images)</span>
                      </label>
                    </div>
                  </div>

                  <button
                    className="scrape-button"
                    onClick={handleManualScrape}
                    disabled={isAnyProcessRunning}
                  >
                    {isScraping ? 'Running Scrape...' : 'Run Manual Scrape'}
                  </button>
                </div>

                {scrapeResult && (
                  <div className={`scrape-status ${scrapeResult.status}`}>
                    <h3>Scrape {scrapeResult.status === 'completed' ? 'Completed' : 'Failed'}</h3>
                    <p className="status-message">{scrapeResult.message}</p>

                    {scrapeResult.metrics && (
                      <div className="metrics-grid">
                        <div className="metric">
                          <span className="metric-value">{scrapeResult.metrics.sourcesChecked}</span>
                          <span className="metric-label">Sources Processed</span>
                        </div>
                        <div className="metric success">
                          <span className="metric-value">✓ {scrapeResult.metrics.successful}</span>
                          <span className="metric-label">Successful</span>
                        </div>
                        <div className="metric error">
                          <span className="metric-value">✗ {scrapeResult.metrics.failed}</span>
                          <span className="metric-label">Failed</span>
                        </div>
                        <div className="metric info">
                          <span className="metric-value">{websitePreviewItems ? '(pending review)' : scrapeResult.metrics.totalItems}</span>
                          <span className="metric-label">Total Items</span>
                        </div>
                      </div>
                    )}

                    {scrapeResult.errorLog && (
                      <div className="error-log-section">
                        <h4>Error Log</h4>
                        <div className="error-log-actions">
                          <button onClick={copyErrorLog} className="error-button copy-button">
                            📋 Copy
                          </button>
                          <button onClick={downloadErrorLog} className="error-button download-button">
                            ⬇️ Download
                          </button>
                        </div>
                        <div className="error-log-preview">
                          <pre>{scrapeResult.errorLog}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Website Scraper Preview Review */}
                {websitePreviewItems && websitePreviewItems.length > 0 && (
                  <div className="extracted-data-section" style={{marginTop: '1rem'}}>
                    <h4>Extracted Items (Review & Select)</h4>
                    <p className="extraction-instructions">
                      Uncheck any items you don't want to save, then click "Save Selected to Database".
                    </p>

                    <div className="extraction-select-all">
                      <button className="select-all-btn" onClick={() => handleSelectAllWebsiteItems(true)}>Select All</button>
                      <button className="select-all-btn" onClick={() => handleSelectAllWebsiteItems(false)}>Deselect All</button>
                      <span className="selection-count">
                        {Object.values(websiteSelectedItems).filter(Boolean).length} / {websitePreviewItems.length} selected
                      </span>
                    </div>

                    {Object.entries(
                      websitePreviewItems.reduce((groups, item, i) => {
                        const venueName = item.restaurant_name || item.source_name || 'Unknown Venue';
                        if (!groups[venueName]) groups[venueName] = [];
                        groups[venueName].push({ ...item, _index: i });
                        return groups;
                      }, {})
                    ).map(([venueName, items]) => {
                      // Group items by effective type (respecting overrides)
                      const byType = items.reduce((types, item) => {
                        const type = getItemType(item);
                        if (!types[type]) types[type] = [];
                        types[type].push(item);
                        return types;
                      }, {});

                      // Render a single preview item with type-override dropdown
                      const renderPreviewItem = (item) => {
                        const effectiveType = getItemType(item);
                        const nameField = item.item_data['Deal Name'] || item.item_data['Special Name'] || item.item_data['Event Name'] || 'Unnamed';
                        return (
                          <label key={item._index} className="extraction-item">
                            <input
                              type="checkbox"
                              checked={websiteSelectedItems[`ws-item-${item._index}`] || false}
                              onChange={() => handleToggleWebsiteItem(`ws-item-${item._index}`)}
                            />
                            <span>
                              <span className={item.is_update ? 'ws-update-badge' : 'ws-new-badge'}>
                                {item.is_update ? 'update' : 'new'}
                              </span>
                              <select
                                className={`ws-type-select ws-type-${effectiveType}`}
                                value={effectiveType}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setWebsiteItemTypeOverrides(prev => ({...prev, [item._index]: e.target.value}));
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="deal">deal</option>
                                <option value="special">special</option>
                                <option value="event">event</option>
                              </select>
                              {' '}{nameField}
                              {item.item_data['Days of Week'] && ` — ${Array.isArray(item.item_data['Days of Week']) ? item.item_data['Days of Week'].join(', ') : item.item_data['Days of Week']}`}
                              {effectiveType !== 'event' && item.item_data['Start Time'] && ` ${item.item_data['Start Time']}`}
                              {effectiveType !== 'event' && item.item_data['End Time'] && `–${item.item_data['End Time']}`}
                              {effectiveType === 'event' && item.item_data['Event Start'] && ` — ${formatPreviewDate(item.item_data['Event Start'])}`}
                              {effectiveType === 'event' && item.item_data['Event Type'] && ` [${item.item_data['Event Type']}]`}
                            </span>
                          </label>
                        );
                      };

                      return (
                        <div key={venueName} className="restaurant-extractions">
                          <h5>{venueName}</h5>

                          {byType.deal && byType.deal.length > 0 && (
                            <div className="extraction-group">
                              <h6>🍸 Happy Hour Deals</h6>
                              {byType.deal.map(renderPreviewItem)}
                            </div>
                          )}

                          {byType.special && byType.special.length > 0 && (
                            <div className="extraction-group">
                              <h6>⭐ Restaurant Specials</h6>
                              {byType.special.map(renderPreviewItem)}
                            </div>
                          )}

                          {byType.event && byType.event.length > 0 && (
                            <div className="extraction-group">
                              <h6>📅 Events</h6>
                              {byType.event.map(renderPreviewItem)}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div className="extraction-actions">
                      <button
                        className="scrape-button"
                        onClick={handleSaveWebsiteItems}
                        disabled={isSavingWebsiteItems}
                      >
                        {isSavingWebsiteItems ? 'Saving...' : 'Save Selected to Database'}
                      </button>
                      <button
                        className="reset-button"
                        onClick={handleDiscardWebsitePreview}
                        disabled={isSavingWebsiteItems}
                      >
                        Discard & Start Over
                      </button>
                    </div>
                  </div>
                )}

              </div>
            )}

                {/* Aggregators Tab */}
                {activeScraperTab === 'aggregators' && (
              <div className="tab-panel">
                <p className="tab-description">
                  Scrape events from aggregator sites like LocalVibz that list multiple venues.
                  Events are automatically matched to existing restaurants. Duplicates are detected and skipped.
                </p>

                <div className="scrape-controls">
                  <div className="form-group">
                    <label htmlFor="aggregator-source">Source:</label>
                    <select
                      id="aggregator-source"
                      value={selectedSourceId}
                      onChange={(e) => {
                        setSelectedSourceId(e.target.value);
                        // Clear manual URL when selecting a source
                        if (e.target.value && e.target.value !== 'all') {
                          const source = aggregatorSources.find(s => s.id === e.target.value);
                          if (source) {
                            setAggregatorUrl(source.fields.URL);
                          }
                        } else if (e.target.value === '') {
                          setAggregatorUrl('');
                        }
                      }}
                      disabled={isScrapingAggregator}
                    >
                      <option value="">-- Select source --</option>
                      <option value="all">🔄 Scrape All Active Sources</option>
                      {aggregatorSources.length > 0 && (
                        <optgroup label="Individual Sources">
                          {aggregatorSources.map(source => (
                            <option key={source.id} value={source.id}>
                              {source.fields.Name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>

                  {selectedSourceId === '' && (
                    <div className="form-group">
                      <label htmlFor="aggregator-url">Or enter URL manually:</label>
                      <input
                        type="text"
                        id="aggregator-url"
                        className="url-input"
                        value={aggregatorUrl}
                        onChange={(e) => setAggregatorUrl(e.target.value)}
                        placeholder="https://localvibz.com/events?q=..."
                        disabled={isScrapingAggregator}
                      />
                    </div>
                  )}

                  {selectedSourceId && selectedSourceId !== 'all' && (
                    <div className="form-group">
                      <label>URL:</label>
                      <div className="url-preview">{aggregatorUrl}</div>
                    </div>
                  )}

                  <button
                    className="scrape-button"
                    onClick={handleAggregatorScrape}
                    disabled={isAnyProcessRunning || (!aggregatorUrl && selectedSourceId !== 'all')}
                  >
                    {isScrapingAggregator
                      ? (aggregatorProgress
                          ? `Scraping ${aggregatorProgress.current}/${aggregatorProgress.total}...`
                          : 'Scraping...')
                      : (selectedSourceId === 'all'
                          ? `Scrape All (${aggregatorSources.length} sources)`
                          : 'Scrape Aggregator')}
                  </button>
                </div>

                {aggregatorProgress && (
                  <div className="progress-indicator">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${(aggregatorProgress.current / aggregatorProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="progress-text">
                      Processing: {aggregatorProgress.currentSource} ({aggregatorProgress.current}/{aggregatorProgress.total})
                    </p>
                  </div>
                )}

                <div className="schedule-note" style={{marginTop: '1rem'}}>
                  <strong>Tip:</strong> Add new sources in the <code>Aggregator Sources</code> table in Airtable.
                </div>

                {aggregatorResult && (
                  <div className={`scrape-status ${aggregatorResult.status}`}>
                    <h3>Scrape {aggregatorResult.status === 'completed' ? 'Completed' : 'Failed'}</h3>
                    <p className="status-message">{aggregatorResult.message}</p>

                    {aggregatorResult.metrics && (
                      <div className="metrics-grid">
                        <div className="metric info">
                          <span className="metric-value">{aggregatorResult.metrics.eventsExtracted || 0}</span>
                          <span className="metric-label">Events Extracted</span>
                        </div>
                        <div className="metric success">
                          <span className="metric-value">{aggregatorPreviewEvents ? '(pending review)' : `✓ ${aggregatorResult.metrics.successfullyWritten || 0}`}</span>
                          <span className="metric-label">Written to DB</span>
                        </div>
                        {aggregatorResult.metrics.sourcesProcessed && (
                          <>
                            <div className="metric info">
                              <span className="metric-value">{aggregatorResult.metrics.sourcesProcessed}</span>
                              <span className="metric-label">Sources Processed</span>
                            </div>
                            {aggregatorResult.metrics.sourcesFailed > 0 && (
                              <div className="metric error">
                                <span className="metric-value">{aggregatorResult.metrics.sourcesFailed}</span>
                                <span className="metric-label">Sources Failed</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {aggregatorResult.errorLog && (
                      <div className="error-log-section">
                        <h4>Details</h4>
                        <div className="error-log-preview">
                          <pre>{aggregatorResult.errorLog}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Aggregator Preview Review */}
                {aggregatorPreviewEvents && aggregatorPreviewEvents.length > 0 && (
                  <div className="extracted-data-section" style={{marginTop: '1rem'}}>
                    <h4>Extracted Events (Review & Select)</h4>
                    <p className="extraction-instructions">
                      Uncheck any events you don't want to save, then click "Save Selected to Database".
                    </p>

                    {Object.entries(
                      aggregatorPreviewEvents.reduce((groups, event, i) => {
                        const venueName = event.venue_name || 'Unknown Venue';
                        if (!groups[venueName]) groups[venueName] = [];
                        groups[venueName].push({ ...event, _index: i });
                        return groups;
                      }, {})
                    ).map(([venueName, events]) => (
                      <div key={venueName} className="restaurant-extractions">
                        <h5>{venueName}</h5>
                        <div className="extraction-group">
                          <h6>Events</h6>
                          {events.map(event => (
                            <label key={event._index} className="extraction-item">
                              <input
                                type="checkbox"
                                checked={aggregatorSelectedEvents[`agg-event-${event._index}`] || false}
                                onChange={() => handleToggleAggregatorEvent(`agg-event-${event._index}`)}
                              />
                              <span>
                                <span className={event.is_update ? 'agg-update-badge' : 'agg-new-badge'}>
                                  {event.is_update ? 'update' : 'new'}
                                </span>
                                {' '}{event.event_data['Event Name']}
                                {event.event_data['Event Start'] &&
                                  ` — ${formatPreviewDate(event.event_data['Event Start'])}`
                                }
                                {event.event_data['Event Type'] &&
                                  ` [${event.event_data['Event Type']}]`
                                }
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="extraction-actions">
                      <button
                        className="scrape-button"
                        onClick={handleSaveAggregatorEvents}
                        disabled={isSavingAggregatorEvents}
                      >
                        {isSavingAggregatorEvents ? 'Saving...' : 'Save Selected to Database'}
                      </button>
                      <button
                        className="reset-button"
                        onClick={handleDiscardAggregatorPreview}
                        disabled={isSavingAggregatorEvents}
                      >
                        Discard & Start Over
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

                {/* Special Scrapers Tab (TCC only) */}
                {activeScraperTab === 'special' && activeAdminMarket.id === 'tcc' && (
                  <div className="tab-panel">
                    <p className="tab-description">
                      Special-purpose scrapers for specific venues that require custom handling beyond standard website scraping.
                    </p>

                    {/* Dry Run / Verbose options for special scrapers */}
                    <div className="special-scraper-options" style={{display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap'}}>
                      <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem'}}>
                        <input
                          type="checkbox"
                          checked={cleanupDryRun}
                          onChange={(e) => setCleanupDryRun(e.target.checked)}
                          disabled={isRunningTool}
                        />
                        <span>Dry run</span>
                        <span className="checkbox-hint" style={{color: '#888', fontSize: '0.78rem'}}>(preview without saving)</span>
                      </label>
                      <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem'}}>
                        <input
                          type="checkbox"
                          checked={toolVerbose}
                          onChange={(e) => setToolVerbose(e.target.checked)}
                          disabled={isRunningTool}
                        />
                        <span>Verbose</span>
                        <span className="checkbox-hint" style={{color: '#888', fontSize: '0.78rem'}}>(detailed output)</span>
                      </label>
                    </div>

                    <div className="special-scrapers-grid">
                      {/* Pollyanna STC */}
                      <div className="special-scraper-card">
                        <div className="special-scraper-header">
                          <span className="special-scraper-icon">🍺</span>
                          <div className="special-scraper-info">
                            <h4>Pollyanna STC</h4>
                            <p className="special-scraper-desc">Scrapes the Pollyanna St. Charles Untappd menu page for current beer offerings.</p>
                          </div>
                        </div>
                        <button
                          className="scrape-button special-scraper-button"
                          onClick={() => handleRunTool('scrape-pollyanna')}
                          disabled={isAnyProcessRunning}
                        >
                          {runningToolType === 'scrape-pollyanna' ? 'Running...' : 'Run Scraper'}
                        </button>
                      </div>

                      {/* STC Alliance Happy Hour Hub */}
                      <div className="special-scraper-card">
                        <div className="special-scraper-header">
                          <span className="special-scraper-icon">🍻</span>
                          <div className="special-scraper-info">
                            <h4>STC Alliance Happy Hour Hub</h4>
                            <p className="special-scraper-desc">Scrapes the St. Charles Business Alliance Happy Hour Hub for deals across ~35 venues. Matches against TCC database and reports unmatched venues.</p>
                          </div>
                        </div>
                        <button
                          className="scrape-button special-scraper-button"
                          onClick={() => handleRunTool('scrape-stc-alliance')}
                          disabled={isAnyProcessRunning}
                        >
                          {runningToolType === 'scrape-stc-alliance' ? 'Running...' : 'Run Scraper'}
                        </button>
                      </div>
                    </div>

                    {/* Tools Console - shown inline in Special Scrapers tab when a special scraper produced output */}
                    {toolConsoleOutput.length > 0 && (lastToolType === 'scrape-pollyanna' || lastToolType === 'scrape-stc-alliance') && (
                      <div className="tools-console-wrapper" style={{marginTop: '1rem'}} ref={specialScraperConsoleRef}>
                        <div className="tools-console-header" onClick={() => setIsToolConsoleExpanded(!isToolConsoleExpanded)}>
                          <h4>Tools Console</h4>
                          <div className="tools-console-actions">
                            <button className="tool-action-btn" onClick={(e) => { e.stopPropagation(); copyToolConsole(); }} title="Copy to clipboard">📋 Copy</button>
                            <button className="tool-action-btn" onClick={(e) => { e.stopPropagation(); downloadToolConsole(); }} title="Download as text file">⬇️ Download</button>
                            <button className="tool-action-btn tool-action-clear" onClick={(e) => { e.stopPropagation(); clearToolConsole(); }} title="Clear console">Clear</button>
                            <button className="collapse-button">{isToolConsoleExpanded ? '▼' : '▶'}</button>
                          </div>
                        </div>
                        {isToolConsoleExpanded && (
                          <div className="terminal-console">
                            <div className="terminal-header">
                              <span className="terminal-title">Tools Output</span>
                              <div className="terminal-dots">
                                <span className="dot red"></span>
                                <span className="dot yellow"></span>
                                <span className="dot green"></span>
                              </div>
                            </div>
                            <div className="terminal-body" ref={toolConsoleRef}>
                              {toolConsoleOutput.map((log, index) => (
                                <div key={index} className={`terminal-line ${log.type}`}>
                                  <span className="terminal-timestamp">[{log.timestamp}]</span>
                                  <span className="terminal-message">{log.message}</span>
                                </div>
                              ))}
                              {isRunningTool && (
                                <div className="terminal-line">
                                  <span className="terminal-cursor">▊</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Inline Console Output for Scraper Management */}
              {consoleOutput.length > 0 && consoleSource === 'scraper' && (
                <div className="inline-console-wrapper">
                  <div className="console-header" onClick={() => setIsConsoleExpanded(!isConsoleExpanded)}>
                    <h4>Console Output</h4>
                    <button className="collapse-button">
                      {isConsoleExpanded ? '▼' : '▶'}
                    </button>
                  </div>
                  {isConsoleExpanded && (
                    <div className="terminal-console">
                      <div className="terminal-header">
                        <span className="terminal-title">Console Output</span>
                        <div className="terminal-dots">
                          <span className="dot red"></span>
                          <span className="dot yellow"></span>
                          <span className="dot green"></span>
                        </div>
                      </div>
                      <div className="terminal-body" ref={terminalBodyRef}>
                        {consoleOutput.map((log, index) => (
                          <div key={index} className={`terminal-line ${log.type}`}>
                            <span className="terminal-timestamp">[{log.timestamp}]</span>
                            <span className="terminal-message">{log.message}</span>
                          </div>
                        ))}
                        {isAnyProcessRunning && (
                          <div className="terminal-line">
                            <span className="terminal-cursor">▊</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* CONTENT UPLOAD SECTION - Collapsible */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="admin-section collapsible-section">
          <div
            className={`collapsible-section-header ${isContentUploadExpanded ? 'expanded' : ''}`}
            onClick={() => setIsContentUploadExpanded(!isContentUploadExpanded)}
          >
            <div className="collapsible-section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Upload photos or paste text from social media posts. AI extracts deals, events, and specials for review before publishing.">i</span> 📱 Content Upload</h2>
              <span className="section-description">Upload photos and text from social media</span>
            </div>
            <button className="collapse-button">{isContentUploadExpanded ? '▼' : '▶'}</button>
          </div>

          {isContentUploadExpanded && (
            <div className="collapsible-section-content">
              {/* Tab Navigation for Uploads */}
              <div className="tab-navigation">
                <button
                  className={`tab-button ${activeUploadTab === 'photos' ? 'active' : ''}`}
                  onClick={() => setActiveUploadTab('photos')}
                >
                  📷 Photo Upload
                </button>
                <button
                  className={`tab-button ${activeUploadTab === 'text' ? 'active' : ''}`}
                  onClick={() => setActiveUploadTab('text')}
                >
                  📝 Text Upload
                </button>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Social Photo Upload Tab */}
                {activeUploadTab === 'photos' && (
                  <div className="tab-panel">
                    <p className="tab-description">
                      Upload photos from Facebook, Instagram, or other social media. The system uses Claude Vision
                      to analyze images and extract happy hours, events, and specials.
                    </p>

                <div className="photo-upload-section" onPaste={handlePaste} tabIndex={-1}>
                  {/* Step 1: Select Restaurant and Choose Photos */}
                  <div className="upload-step">
                    <div className="step-header">
                      <span className="step-number">1</span>
                      <span className="step-title">Select Restaurant & Add Photos</span>
                    </div>
                    <div className="upload-controls">
                      <div className="form-group">
                        <label htmlFor="restaurant-select">Restaurant:</label>
                        <select
                          id="restaurant-select"
                          value={selectedRestaurant}
                          onChange={(e) => {
                            setSelectedRestaurant(e.target.value);
                            setPendingFiles([]); // Clear pending when switching restaurants
                          }}
                          disabled={isProcessingPhotos}
                        >
                          <option value="">-- Select restaurant --</option>
                          {restaurants.map(r => (
                            <option key={r.id} value={r.id}>{r.Name} ({r.City})</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group file-upload-group">
                        <input
                          type="file"
                          id="photo-upload"
                          accept="image/*"
                          multiple
                          onChange={handleFileSelect}
                          disabled={!selectedRestaurant || isProcessingPhotos}
                          className="file-input"
                        />
                        <label htmlFor="photo-upload" className={`file-upload-button ${!selectedRestaurant ? 'disabled' : ''}`}>
                          📷 Choose Photos
                        </label>
                        {selectedRestaurant && (
                          <span className="paste-hint">or paste from clipboard (Ctrl+V / Cmd+V)</span>
                        )}
                      </div>
                    </div>

                    {/* Pending Files Preview */}
                    {pendingFiles.length > 0 && (
                      <div className="pending-files">
                        <div className="pending-header">
                          <span>Selected {pendingFiles.length} photo(s) for {restaurants.find(r => r.id === selectedRestaurant)?.Name}</span>
                          <button className="clear-pending-button" onClick={handleClearPending}>
                            Clear
                          </button>
                        </div>
                        <div className="pending-file-list">
                          {pendingFiles.map((file, i) => (
                            <div key={i} className="pending-file">
                              <span className="file-name">{file.name}</span>
                              <button
                                className="remove-file"
                                onClick={() => handleRemovePendingFile(i)}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          className="add-to-queue-button"
                          onClick={handleAddToQueue}
                          disabled={isProcessingPhotos}
                        >
                          + Add to Queue
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Step 2: Review Queue */}
                  <div className="upload-step">
                    <div className="step-header">
                      <span className="step-number">2</span>
                      <span className="step-title">Review Queue & Process</span>
                      {uploadQueue.length > 0 && (
                        <span className="queue-badge">{uploadQueue.length} restaurant(s)</span>
                      )}
                    </div>

                    {uploadQueue.length === 0 ? (
                      <div className="empty-queue-message">
                        <p>No restaurants in queue yet. Select a restaurant, add photos, then click "Add to Queue".</p>
                      </div>
                    ) : (
                      <div className="upload-queue">
                        <div className="queue-header">
                          <h4>
                            {uploadQueue.length} Restaurant(s) • {uploadQueue.reduce((sum, q) => sum + q.files.length, 0)} Total Photos
                          </h4>
                          <button className="clear-queue-button" onClick={handleClearQueue} disabled={isProcessingPhotos}>
                            Clear All
                          </button>
                        </div>
                        <div className="queue-items">
                          {uploadQueue.map(queueItem => (
                            <div key={queueItem.restaurantId} className="queue-item">
                              <div className="queue-item-header">
                                <span className="queue-restaurant-name">{queueItem.restaurantName}</span>
                                <span className="queue-file-count">{queueItem.files.length} photo(s)</span>
                                <button
                                  className="remove-queue-item"
                                  onClick={() => handleRemoveFromQueue(queueItem.restaurantId)}
                                  disabled={isProcessingPhotos}
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="queue-files">
                                {queueItem.files.map((file, i) => (
                                  <div key={i} className="queue-file">
                                    <span className="file-name">{file.name}</span>
                                    <button
                                      className="remove-file"
                                      onClick={() => handleRemoveFromQueue(queueItem.restaurantId, i)}
                                      disabled={isProcessingPhotos}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          className="scrape-button process-photos-button"
                          onClick={handleProcessPhotos}
                          disabled={isAnyProcessRunning}
                          style={{ width: '100%', marginTop: '1rem' }}
                        >
                          {isProcessingPhotos ? 'Processing...' : `Process All (${uploadQueue.reduce((sum, q) => sum + q.files.length, 0)} photos)`}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Extracted Data Review */}
                  {extractedData && extractedData.length > 0 && (
                    <div className="extracted-data-section">
                      <h4>Extracted Data (Review & Select)</h4>
                      <p className="extraction-instructions">
                        Uncheck any items you don't want to save, then click "Save Selected to Database".
                      </p>

                      {extractedData.map(result => {
                        const renderExtractionItem = (item, bucketKey, idx) => {
                          const key = `${result.restaurantId}-${bucketKey}-${idx}`;
                          const effType = getExtractionType(extractionTypeOverrides, result.restaurantId, bucketKey, idx);
                          const label =
                            bucketKey === 'hh' ? (item.name || item.description || 'Happy Hour') :
                            bucketKey === 'event' ? (item.name || item.description || 'Event') :
                            (item.name || item.description || 'Special');
                          const suffix =
                            bucketKey === 'hh' && item.time ? ` - ${item.time}` :
                            bucketKey === 'event' && item.date ? ` - ${item.date}` :
                            '';
                          return (
                            <label key={idx} className="extraction-item">
                              <input
                                type="checkbox"
                                checked={selectedExtractions[key] || false}
                                onChange={() => handleToggleExtraction(key)}
                              />
                              <span>
                                <select
                                  className={`ws-type-select ws-type-${effType}`}
                                  value={effType}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setExtractionTypeOverrides(prev => ({ ...prev, [key]: e.target.value }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="deal">deal</option>
                                  <option value="special">special</option>
                                  <option value="event">event</option>
                                </select>
                                {' '}{label}{suffix}
                              </span>
                            </label>
                          );
                        };

                        return (
                          <div key={result.restaurantId} className="restaurant-extractions">
                            <h5>{result.restaurantName}</h5>

                            {result.happyHours && result.happyHours.length > 0 && (
                              <div className="extraction-group">
                                <h6>Happy Hours</h6>
                                {result.happyHours.map((hh, i) => renderExtractionItem(hh, 'hh', i))}
                              </div>
                            )}

                            {result.events && result.events.length > 0 && (
                              <div className="extraction-group">
                                <h6>Events</h6>
                                {result.events.map((event, i) => renderExtractionItem(event, 'event', i))}
                              </div>
                            )}

                            {result.specials && result.specials.length > 0 && (
                              <div className="extraction-group">
                                <h6>Specials</h6>
                                {result.specials.map((special, i) => renderExtractionItem(special, 'special', i))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      <div className="extraction-actions">
                        <button
                          className="scrape-button"
                          onClick={handleSaveExtractions}
                          disabled={isProcessingPhotos}
                        >
                          {isProcessingPhotos ? 'Saving...' : 'Save Selected to Database'}
                        </button>
                        <button
                          className="reset-button"
                          onClick={handleClearQueue}
                          disabled={isProcessingPhotos}
                        >
                          Discard & Start Over
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

                {/* Social Text Upload Tab */}
                {activeUploadTab === 'text' && (
              <div className="tab-panel">
                <p className="tab-description">
                  Paste text copied from Facebook, Instagram, websites, or other sources. The system uses Claude AI
                  to analyze the text and extract happy hours, events, and specials. Emojis are automatically stripped before processing.
                </p>

                <div className="text-upload-section">
                  {/* Step 1: Select Restaurant and Paste Text */}
                  <div className="upload-step">
                    <div className="step-header">
                      <span className="step-number">1</span>
                      <span className="step-title">Select Restaurant & Paste Text</span>
                    </div>

                    <div className="text-upload-controls">
                      <div className="form-group">
                        <label htmlFor="text-restaurant-select">Restaurant:</label>
                        <select
                          id="text-restaurant-select"
                          value={textRestaurant}
                          onChange={(e) => setTextRestaurant(e.target.value)}
                          disabled={isProcessingText}
                        >
                          <option value="">-- Select restaurant --</option>
                          {restaurants.map(r => (
                            <option key={r.id} value={r.id}>{r.Name} ({r.City})</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group text-input-group">
                        <label htmlFor="text-paste-area">Paste text here:</label>
                        <textarea
                          id="text-paste-area"
                          className="text-paste-area"
                          value={pastedText}
                          onChange={(e) => setPastedText(e.target.value)}
                          placeholder="Paste text from social media, websites, emails, etc. For example: 'Happy Hour Mon-Fri 4-6pm, $5 drafts, $7 wells. Live Music every Friday at 8pm...'"
                          disabled={isProcessingText}
                          rows={8}
                        />
                        {pastedText && (
                          <div className="text-char-count">
                            {pastedText.length} characters
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Process */}
                  <div className="upload-step">
                    <div className="step-header">
                      <span className="step-number">2</span>
                      <span className="step-title">Process & Review</span>
                    </div>

                    <div className="text-process-controls">
                      <button
                        className="scrape-button process-text-button"
                        onClick={handleProcessText}
                        disabled={isAnyProcessRunning || !textRestaurant || !pastedText.trim()}
                      >
                        {isProcessingText ? 'Processing...' : 'Process Text'}
                      </button>
                      {(pastedText || textExtractedData) && (
                        <button
                          className="reset-button"
                          onClick={handleClearTextUpload}
                          disabled={isProcessingText}
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    {!textRestaurant && !pastedText.trim() && (
                      <div className="empty-queue-message" style={{marginTop: '1rem'}}>
                        <p>Select a restaurant and paste text above, then click "Process Text".</p>
                      </div>
                    )}
                  </div>

                  {/* Extracted Data Review */}
                  {textExtractedData && textExtractedData.length > 0 && (
                    <div className="extracted-data-section">
                      <h4>Extracted Data (Review & Select)</h4>
                      <p className="extraction-instructions">
                        Uncheck any items you don't want to save, then click "Save Selected to Database".
                      </p>

                      {textExtractedData.map(result => {
                        const renderTextExtractionItem = (item, bucketKey, idx) => {
                          const key = `${result.restaurantId}-${bucketKey}-${idx}`;
                          const effType = getExtractionType(textExtractionTypeOverrides, result.restaurantId, bucketKey, idx);
                          const label =
                            bucketKey === 'hh' ? (item.name || item.description || 'Happy Hour') :
                            bucketKey === 'event' ? (item.name || item.description || 'Event') :
                            (item.name || item.description || 'Special');
                          const suffix =
                            bucketKey === 'hh' && item.time ? ` - ${item.time}` :
                            bucketKey === 'event' && item.date ? ` - ${item.date}` :
                            '';
                          return (
                            <label key={idx} className="extraction-item">
                              <input
                                type="checkbox"
                                checked={textSelectedExtractions[key] || false}
                                onChange={() => handleTextToggleExtraction(key)}
                              />
                              <span>
                                <select
                                  className={`ws-type-select ws-type-${effType}`}
                                  value={effType}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setTextExtractionTypeOverrides(prev => ({ ...prev, [key]: e.target.value }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="deal">deal</option>
                                  <option value="special">special</option>
                                  <option value="event">event</option>
                                </select>
                                {' '}{label}{suffix}
                              </span>
                            </label>
                          );
                        };

                        return (
                          <div key={result.restaurantId} className="restaurant-extractions">
                            <h5>{result.restaurantName}</h5>

                            {result.happyHours && result.happyHours.length > 0 && (
                              <div className="extraction-group">
                                <h6>Happy Hours</h6>
                                {result.happyHours.map((hh, i) => renderTextExtractionItem(hh, 'hh', i))}
                              </div>
                            )}

                            {result.events && result.events.length > 0 && (
                              <div className="extraction-group">
                                <h6>Events</h6>
                                {result.events.map((event, i) => renderTextExtractionItem(event, 'event', i))}
                              </div>
                            )}

                            {result.specials && result.specials.length > 0 && (
                              <div className="extraction-group">
                                <h6>Specials</h6>
                                {result.specials.map((special, i) => renderTextExtractionItem(special, 'special', i))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      <div className="extraction-actions">
                        <button
                          className="scrape-button"
                          onClick={handleSaveTextExtractions}
                          disabled={isProcessingText}
                        >
                          {isProcessingText ? 'Saving...' : 'Save Selected to Database'}
                        </button>
                        <button
                          className="reset-button"
                          onClick={handleClearTextUpload}
                          disabled={isProcessingText}
                        >
                          Discard & Start Over
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
                )}
              </div>

              {/* Inline Console Output for Content Upload */}
              {consoleOutput.length > 0 && consoleSource === 'upload' && (
                <div className="inline-console-wrapper">
                  <div className="console-header" onClick={() => setIsConsoleExpanded(!isConsoleExpanded)}>
                    <h4>Console Output</h4>
                    <button className="collapse-button">
                      {isConsoleExpanded ? '▼' : '▶'}
                    </button>
                  </div>
                  {isConsoleExpanded && (
                    <div className="terminal-console">
                      <div className="terminal-header">
                        <span className="terminal-title">Console Output</span>
                        <div className="terminal-dots">
                          <span className="dot red"></span>
                          <span className="dot yellow"></span>
                          <span className="dot green"></span>
                        </div>
                      </div>
                      <div className="terminal-body" ref={terminalBodyRef}>
                        {consoleOutput.map((log, index) => (
                          <div key={index} className={`terminal-line ${log.type}`}>
                            <span className="terminal-timestamp">[{log.timestamp}]</span>
                            <span className="terminal-message">{log.message}</span>
                          </div>
                        ))}
                        {isAnyProcessRunning && (
                          <div className="terminal-line">
                            <span className="terminal-cursor">▊</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TOOLS & SETTINGS SECTION - Collapsible (Admin/Super Admin only) */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className={`collapsible-section-header ${isToolsSettingsExpanded ? 'expanded' : ''}`}
            onClick={() => setIsToolsSettingsExpanded(!isToolsSettingsExpanded)}
          >
            <div className="collapsible-section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Database maintenance tools, cleanup utilities, test runner, and app-wide feature toggles like carousel and advertising settings.">i</span> 🛠️ Tools & Settings</h2>
              <span className="section-description">Database tools and app configuration</span>
            </div>
            <button className="collapse-button">{isToolsSettingsExpanded ? '▼' : '▶'}</button>
          </div>

          {isToolsSettingsExpanded && (
            <div className="collapsible-section-content">
              {/* TOOLS subsection - Collapsible */}
              <div className="tools-subsection">
                <div className="settings-header" onClick={() => setIsDatabaseToolsExpanded(!isDatabaseToolsExpanded)}>
                  <div className="settings-header-left">
                    <h3 className="subsection-title">Database Tools</h3>
                    <span className="settings-header-hint">Run validation and maintenance tasks on the database.</span>
                  </div>
                  <button className="collapse-button">{isDatabaseToolsExpanded ? '▼' : '▶'}</button>
                </div>

                {isDatabaseToolsExpanded && (
                <div className="database-tools-content">
                <div className="tools-buttons">
                  <button
                    className="scrape-button tool-button"
                    onClick={() => handleRunTool('validate-data')}
                    disabled={isAnyProcessRunning}
                  >
                    {runningToolType === 'validate-data' ? 'Running...' : '🔍 Run Data Validation'}
                  </button>
                  <button
                    className="scrape-button tool-button"
                    onClick={() => handleRunTool('validate-filters')}
                    disabled={isAnyProcessRunning}
                  >
                    {runningToolType === 'validate-filters' ? 'Running...' : '🧪 Run Filter Tests'}
                  </button>
                  <button
                    className="scrape-button tool-button"
                    onClick={() => handleRunTool('cleanup-events')}
                    disabled={isAnyProcessRunning}
                  >
                    {runningToolType === 'cleanup-events' ? 'Running...' : '🧹 Clean Up Past Events'}
                  </button>
                  <button
                    className="scrape-button tool-button"
                    onClick={() => handleRunTool('audit-descriptions')}
                    disabled={isAnyProcessRunning}
                  >
                    {runningToolType === 'audit-descriptions' ? 'Running...' : '🔍 Audit Descriptions'}
                  </button>
                </div>

                <div className="form-group checkbox-group" style={{marginTop: '0.75rem'}}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={toolVerbose}
                      onChange={(e) => setToolVerbose(e.target.checked)}
                      disabled={isRunningTool}
                    />
                    <span>Verbose mode</span>
                    <span className="checkbox-hint">(show detailed output per venue)</span>
                  </label>
                </div>
                <div className="form-group checkbox-group" style={{marginTop: '0.5rem'}}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={cleanupDryRun}
                      onChange={(e) => setCleanupDryRun(e.target.checked)}
                      disabled={isRunningTool}
                    />
                    <span>Dry run mode</span>
                    <span className="checkbox-hint">(preview changes without saving/deleting)</span>
                  </label>
                </div>

                {/* Tools Console - only show here for database tools, not special scrapers */}
                {toolConsoleOutput.length > 0 && lastToolType !== 'scrape-pollyanna' && lastToolType !== 'scrape-stc-alliance' && (
                  <div className="tools-console-wrapper">
                    <div className="tools-console-header" onClick={() => setIsToolConsoleExpanded(!isToolConsoleExpanded)}>
                      <h4>Tools Console</h4>
                      <div className="tools-console-actions">
                        <button className="tool-action-btn" onClick={(e) => { e.stopPropagation(); copyToolConsole(); }} title="Copy to clipboard">📋 Copy</button>
                        <button className="tool-action-btn" onClick={(e) => { e.stopPropagation(); downloadToolConsole(); }} title="Download as text file">⬇️ Download</button>
                        <button className="tool-action-btn tool-action-clear" onClick={(e) => { e.stopPropagation(); clearToolConsole(); }} title="Clear console">Clear</button>
                        <button className="collapse-button">{isToolConsoleExpanded ? '▼' : '▶'}</button>
                      </div>
                    </div>
                    {isToolConsoleExpanded && (
                      <div className="terminal-console">
                        <div className="terminal-header">
                          <span className="terminal-title">Tools Output</span>
                          <div className="terminal-dots">
                            <span className="dot red"></span>
                            <span className="dot yellow"></span>
                            <span className="dot green"></span>
                          </div>
                        </div>
                        <div className="terminal-body" ref={toolConsoleRef}>
                          {toolConsoleOutput.map((log, index) => (
                            <div key={index} className={`terminal-line ${log.type}`}>
                              <span className="terminal-timestamp">[{log.timestamp}]</span>
                              <span className="terminal-message">{log.message}</span>
                            </div>
                          ))}
                          {isRunningTool && (
                            <div className="terminal-line">
                              <span className="terminal-cursor">▊</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                </div>
                )}
              </div>

              {/* Divider */}
              <hr className="tools-settings-divider" />

              {/* SETTINGS subsection - Collapsible */}
              <div className="settings-subsection">
                <div className="settings-header" onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}>
                  <div className="settings-header-left">
                    <h3 className="subsection-title">Settings</h3>
                    <span className="settings-header-hint">Live Airtable settings — changes save immediately.</span>
                  </div>
                  <button className="collapse-button">{isSettingsExpanded ? '▼' : '▶'}</button>
                </div>

                {isSettingsExpanded && (
                  settingsLoading ? (
                    <div className="metrics-loading"><div className="spinner"></div><p>Loading settings...</p></div>
                  ) : (
                    <div className="settings-list">
                      <div className="settings-group">
                        <h4 className="settings-group-title">Advertising</h4>
                        {renderSettingRow('advertising', 'Enabled', 'Enabled', 'toggle', 'Show or hide banner ads across the app. When off, no ads are displayed to users.')}
                        {renderSettingRow('ad_frequency', 'Ad Frequency', 'Value', 'number', 'How often ads appear in the venue list. Lower number = more frequent. For example, 5 means an ad every 5 venues.')}
                      </div>
                      <div className="settings-group">
                        <h4 className="settings-group-title">Carousels</h4>
                        {renderSettingRow('show_events_carousel', 'Events Carousel', 'Enabled', 'toggle', 'Show or hide the events carousel on the home page. Events will still exist in the database.')}
                        {renderSettingRow('show_happyhour_carousel', 'Happy Hour Carousel', 'Enabled', 'toggle', 'Show or hide the happy hour deals carousel on the home page.')}
                        {renderSettingRow('carousel_scroll_speed', 'Scroll Speed', 'Value', 'number', 'Auto-scroll speed for carousels in seconds. Higher = slower scrolling between items.')}
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Divider */}
              <hr className="tools-settings-divider" />

              {/* KEYWORD MANAGER subsection */}
              <div className="settings-subsection">
                <div className="settings-header" onClick={() => setIsKeywordManagerExpanded(!isKeywordManagerExpanded)}>
                  <div className="settings-header-left">
                    <h3 className="subsection-title">🏷️ Keyword Manager</h3>
                    <span className="settings-header-hint">Customize food &amp; drink category keywords for deal parsing.</span>
                  </div>
                  <button className="collapse-button">{isKeywordManagerExpanded ? '▼' : '▶'}</button>
                </div>
                {isKeywordManagerExpanded && (
                  <div className="km-subsection">
                    <p className="km-description">
                      Customize which words classify deal items as <strong>Drinks</strong> or <strong>Food</strong>. Base keywords (greyed) are built-in and cannot be removed. Custom keywords you add are saved to Airtable and applied globally.
                    </p>
                    <div className="km-columns">
                      {['drink', 'food'].map(type => {
                        const baseList = type === 'food' ? FOOD_KEYWORDS : DRINK_KEYWORDS;
                        const customList = type === 'food' ? customFoodKeywords : customDrinkKeywords;
                        const label = type === 'food' ? 'Food' : 'Drinks';
                        return (
                          <div key={type} className="km-column">
                            <h4 className="km-col-title">{label} Keywords</h4>
                            <div className="km-add-row">
                              <input
                                className="km-input"
                                type="text"
                                placeholder={`Add ${label.toLowerCase()} keyword…`}
                                value={keywordInput[type]}
                                onChange={e => setKeywordInput(prev => ({ ...prev, [type]: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && handleAddKeyword(type)}
                              />
                              <button className="km-add-btn" onClick={() => handleAddKeyword(type)}>Add</button>
                            </div>
                            <div className="km-tag-list">
                              {customList.map(kw => (
                                <span key={kw} className="km-tag km-tag-custom">
                                  {kw}
                                  <button className="km-tag-remove" onClick={() => handleRemoveKeyword(type, kw)} title="Remove">×</button>
                                </span>
                              ))}
                              {baseList.map(kw => (
                                <span key={kw} className="km-tag km-tag-base" title="Built-in keyword">{kw}</span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="km-footer">
                      <button
                        className="admin-pill-button"
                        onClick={handleSaveKeywords}
                        disabled={keywordSaving}
                      >
                        {keywordSaving ? 'Saving…' : 'Save Keywords'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* CONTENT REVIEW SECTION - Admin/Super Admin only (new auth) */}
        {/* ═══════════════════════════════════════════════════════ */}
        {useNewAuth && isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className="section-header-toggle"
            onClick={() => setIsContentReviewExpanded(!isContentReviewExpanded)}
          >
            <div className="section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Review content submitted by venue owners. Approve to publish, request revisions, or reject submissions before they go live.">i</span> 📋 Content Review</h2>
              {reviewPendingCount > 0 && (
                <span className="section-badge cr-badge-pending">{reviewPendingCount} pending</span>
              )}
              {reviewPendingCount === 0 && (
                <span className="section-badge">No pending</span>
              )}
            </div>
            <button className="collapse-button">
              {isContentReviewExpanded ? '▼' : '▶'}
            </button>
          </div>

          {isContentReviewExpanded && (
            <div className="section-content">
              {/* Filters */}
              <div className="cr-filters">
                <label>Status:</label>
                <select
                  value={reviewFilter}
                  onChange={(e) => setReviewFilter(e.target.value)}
                  className="cr-filter-select"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="revision_requested">Revision Requested</option>
                  <option value="all">All</option>
                </select>
                <button className="cr-refresh-btn" onClick={fetchReviewSubmissions} disabled={reviewLoading}>
                  {reviewLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {/* Error */}
              {reviewError && (
                <div className="cr-error">Failed to load submissions: {reviewError}</div>
              )}

              {/* Loading */}
              {reviewLoading && (
                <div className="cr-loading">Loading submissions...</div>
              )}

              {/* Empty state */}
              {!reviewLoading && !reviewError && reviewSubmissions.length === 0 && (
                <div className="cr-empty">
                  No {reviewFilter === 'all' ? '' : reviewFilter} submissions found.
                </div>
              )}

              {/* Submission list */}
              {!reviewLoading && reviewSubmissions.length > 0 && (
                <div className="cr-list">
                  {reviewSubmissions.map(sub => (
                    <div key={sub.id} className={`cr-card ${expandedSubmission === sub.id ? 'cr-card-expanded' : ''}${sub.isPriority ? ' cr-card-priority' : ''}`}>
                      <div
                        className="cr-card-header"
                        onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}
                      >
                        <div className="cr-card-info">
                          <span className={`cr-status cr-status-${sub.status}`}>
                            {sub.status === 'pending' ? 'Pending' :
                             sub.status === 'approved' ? 'Approved' :
                             sub.status === 'rejected' ? 'Rejected' :
                             'Revision Requested'}
                          </span>
                          {sub.isPriority && <span className="cr-priority-badge">Priority</span>}
                          {sub.extractedData?.requestType === 'edit_request' && (
                            <span className="cr-request-type-badge cr-request-type-edit">Edit Request</span>
                          )}
                          {sub.extractedData?.requestType === 'removal_request' && (
                            <span className="cr-request-type-badge cr-request-type-removal">Removal Request</span>
                          )}
                          {sub.extractedData?.requestType === 'add_new' && (
                            <span className="cr-request-type-badge cr-request-type-new">New Content</span>
                          )}
                          <strong className="cr-restaurant-name">{sub.extractedData?.originalContentName || sub.restaurantName || 'Unknown Venue'}</strong>
                          <span className="cr-content-type">{sub.contentType}</span>
                          <span className="cr-source-type">{sub.sourceType}</span>
                        </div>
                        <div className="cr-card-meta">
                          <span className="cr-date">
                            {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                          </span>
                          <span className="cr-expand-icon">{expandedSubmission === sub.id ? '▼' : '▶'}</span>
                        </div>
                      </div>

                      {expandedSubmission === sub.id && (
                        <div className="cr-card-detail">
                          {/* Extracted Data Preview */}
                          <div className="cr-data-preview">
                            <h4>Extracted Content</h4>
                            {sub.extractedData && (
                              (sub.extractedData.requestType === 'edit_request' || sub.extractedData.requestType === 'removal_request') ? (
                                  <div className="cr-request-info">
                                    <div className={`cr-request-badge ${sub.extractedData.requestType === 'edit_request' ? 'cr-request-edit' : 'cr-request-removal'}`}>
                                      {sub.extractedData.requestType === 'edit_request' ? 'Edit Request' : 'Removal Request'}
                                    </div>
                                    <div className="cr-request-detail">
                                      <strong>Content:</strong>{' '}
                                      {sub.extractedData.originalContentType === 'deal' ? 'Happy Hour' :
                                       sub.extractedData.originalContentType === 'event' ? 'Event' :
                                       sub.extractedData.originalContentType === 'special' ? 'Special' :
                                       sub.extractedData.originalContentType || 'Unknown'}{' '}
                                      — {sub.extractedData.originalContentName || 'Untitled'}
                                    </div>

                                    {/* Field-level diff view for structured edit requests */}
                                    {sub.extractedData.editedFields && Object.keys(sub.extractedData.editedFields).length > 0 && (
                                      <div className="cr-diff-view">
                                        <h5>Proposed Changes</h5>
                                        {Object.entries(sub.extractedData.editedFields).map(([field, newValue]) => {
                                          const oldValue = sub.extractedData.originalFields?.[field];
                                          const fmt = (val) => {
                                            if (val === null || val === undefined || val === '') return '(empty)';
                                            if (Array.isArray(val)) return val.join(', ');
                                            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                                            return String(val);
                                          };
                                          return (
                                            <div key={field} className="cr-diff-field">
                                              <span className="cr-diff-label">{field}</span>
                                              <div className="cr-diff-values">
                                                <span className="cr-diff-old">{fmt(oldValue)}</span>
                                                <span className="cr-diff-arrow">&rarr;</span>
                                                <span className="cr-diff-new">{fmt(newValue)}</span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {sub.extractedData.notes && (
                                      <div className="cr-request-notes">
                                        <strong>Venue owner's note:</strong>
                                        <p>{sub.extractedData.notes}</p>
                                      </div>
                                    )}
                                    {sub.extractedData.requestType === 'edit_request' && (
                                      sub.extractedData.editedFields && Object.keys(sub.extractedData.editedFields).length > 0 ? (
                                        <p className="cr-request-hint">Approving will apply the changes shown above to the live database.</p>
                                      ) : (
                                        <p className="cr-request-hint">Approving acknowledges this request. Make the edit manually in Airtable, then approve.</p>
                                      )
                                    )}
                                    {sub.extractedData.requestType === 'removal_request' && (
                                      <p className="cr-request-hint">Approving will delete this content from the live database.</p>
                                    )}
                                  </div>
                              ) : sub.extractedData.requestType === 'add_new' ? (
                                  <div className="cr-request-info">
                                    <div className="cr-request-badge cr-request-new">New Content</div>
                                    <div className="cr-request-detail">
                                      <strong>Type:</strong>{' '}
                                      {sub.extractedData.contentType === 'deal' ? 'Happy Hour Deal' :
                                       sub.extractedData.contentType === 'event' ? 'Event' :
                                       sub.extractedData.contentType === 'special' ? 'Special' :
                                       sub.extractedData.contentType || 'Unknown'}
                                      {' — '}{sub.restaurantName || 'Unknown Venue'}
                                    </div>

                                    {/* Show all fields for new content */}
                                    {sub.extractedData.fields && Object.keys(sub.extractedData.fields).length > 0 && (
                                      <div className="cr-diff-view">
                                        <h5>New Content Details</h5>
                                        {Object.entries(sub.extractedData.fields).map(([field, value]) => {
                                          const fmt = (val) => {
                                            if (val === null || val === undefined || val === '') return '(empty)';
                                            if (Array.isArray(val)) return val.join(', ');
                                            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                                            return String(val);
                                          };
                                          return (
                                            <div key={field} className="cr-diff-field">
                                              <span className="cr-diff-label">{field}</span>
                                              <div className="cr-diff-values">
                                                <span className="cr-diff-new">{fmt(value)}</span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {sub.extractedData.image && (
                                      <div style={{ margin: '0.75rem 0' }}>
                                        <strong style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Attached Image:</strong>
                                        <img
                                          src={sub.extractedData.image.imageId
                                            ? `${process.env.REACT_APP_API_URL || ''}/api/pending-image/${sub.extractedData.image.imageId}`
                                            : `data:${sub.extractedData.image.mimetype};base64,${sub.extractedData.image.base64}`}
                                          alt="Event flyer"
                                          style={{ maxWidth: '200px', borderRadius: '8px', border: '1px solid #ddd' }}
                                        />
                                      </div>
                                    )}

                                    {sub.extractedData.notes && (
                                      <div className="cr-request-notes">
                                        <strong>Venue owner's note:</strong>
                                        <p>{sub.extractedData.notes}</p>
                                      </div>
                                    )}

                                    <p className="cr-request-hint">Approving will create a new record in the live database linked to {sub.restaurantName || 'this venue'}.</p>
                                  </div>
                                ) : (
                                <div>
                                {(sub.extractedData.happyHours?.length > 0 || sub.extractedData.deals?.length > 0) && (
                                  <div className="cr-data-group">
                                    <h5>Happy Hour Deals</h5>
                                    {(sub.extractedData.happyHours || sub.extractedData.deals || []).map((item, i) => (
                                      <div key={i} className="cr-data-item">
                                        <strong>{item.name || item.deal_name || 'Untitled'}</strong>
                                        {item.description && <p>{item.description}</p>}
                                        {(item.days || item.time) && <span className="cr-data-meta">{item.days} {item.time}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {sub.extractedData.events?.length > 0 && (
                                  <div className="cr-data-group">
                                    <h5>Events</h5>
                                    {sub.extractedData.events.map((item, i) => (
                                      <div key={i} className="cr-data-item">
                                        <strong>{item.name || item.event_name || 'Untitled'}</strong>
                                        {item.description && <p>{item.description}</p>}
                                        {(item.date || item.type) && <span className="cr-data-meta">{item.type} {item.date}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {sub.extractedData.specials?.length > 0 && (
                                  <div className="cr-data-group">
                                    <h5>Specials</h5>
                                    {sub.extractedData.specials.map((item, i) => (
                                      <div key={i} className="cr-data-item">
                                        <strong>{item.name || item.special_name || 'Untitled'}</strong>
                                        {item.description && <p>{item.description}</p>}
                                        {item.days && <span className="cr-data-meta">{item.days}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {!sub.extractedData.happyHours?.length && !sub.extractedData.deals?.length && !sub.extractedData.events?.length && !sub.extractedData.specials?.length && (
                                  <pre className="cr-raw-json">{JSON.stringify(sub.extractedData, null, 2)}</pre>
                                )}
                                </div>
                                )
                            )}
                          </div>

                          {/* Previous review notes */}
                          {sub.reviewNotes && (
                            <div className="cr-previous-notes">
                              <strong>Previous Review Notes:</strong> {sub.reviewNotes}
                            </div>
                          )}

                          {/* Review Actions (only for pending/revision_requested) */}
                          {(sub.status === 'pending' || sub.status === 'revision_requested') && (
                            <div className="cr-actions">
                              <div className="cr-notes-input">
                                <label>Review Notes:</label>
                                <textarea
                                  value={reviewNotes}
                                  onChange={(e) => setReviewNotes(e.target.value)}
                                  placeholder="Add notes (required for reject/revision)..."
                                  rows={3}
                                />
                              </div>
                              <div className="cr-action-buttons">
                                <button
                                  className="cr-btn cr-btn-approve"
                                  onClick={() => handleReviewAction(sub.id, 'approve')}
                                  disabled={!!reviewActionLoading}
                                >
                                  {reviewActionLoading === sub.id ? 'Processing...' : 'Approve & Publish'}
                                </button>
                                <button
                                  className="cr-btn cr-btn-revision"
                                  onClick={() => handleReviewAction(sub.id, 'revision')}
                                  disabled={!!reviewActionLoading}
                                >
                                  Request Revision
                                </button>
                                <button
                                  className="cr-btn cr-btn-reject"
                                  onClick={() => handleReviewAction(sub.id, 'reject')}
                                  disabled={!!reviewActionLoading}
                                >
                                  Reject
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* AUDIT LOG SECTION - Admin/Super Admin only (new auth) */}
        {/* ═══════════════════════════════════════════════════════ */}
        {useNewAuth && isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className="section-header-toggle"
            onClick={() => setIsAuditLogExpanded(!isAuditLogExpanded)}
          >
            <div className="section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Timestamped log of all user actions — logins, content uploads, approvals, setting changes, and account updates across all markets.">i</span> 📜 Audit Log</h2>
            </div>
            <button className="collapse-button">
              {isAuditLogExpanded ? '▼' : '▶'}
            </button>
          </div>

          {isAuditLogExpanded && (
            <div className="section-content">
              {/* Filters */}
              <div className="al-filters">
                <div className="al-filter-row">
                  <label>Action:</label>
                  <select
                    value={auditFilterAction}
                    onChange={(e) => setAuditFilterAction(e.target.value)}
                    className="al-filter-select"
                  >
                    <option value="">All Actions</option>
                    <option value="login">Login</option>
                    <option value="logout">Logout</option>
                    <option value="scrape_triggered">Scrape Triggered</option>
                    <option value="content_uploaded">Content Uploaded</option>
                    <option value="content_approved">Content Approved</option>
                    <option value="content_rejected">Content Rejected</option>
                    <option value="setting_changed">Setting / Venue Changed</option>
                    <option value="user_created">User Created</option>
                    <option value="user_updated">User Updated</option>
                    <option value="password_changed">Password Changed</option>
                  </select>

                  {isSuperAdmin && (
                    <>
                      <label>Market:</label>
                      <select
                        value={auditFilterMarket}
                        onChange={(e) => setAuditFilterMarket(e.target.value)}
                        className="al-filter-select"
                      >
                        <option value="">All Markets</option>
                        {Object.entries(allMarkets).filter(([k]) => !k.startsWith('otc-example')).map(([key, m]) => (
                          <option key={key} value={m.id === 'otc-naperville' ? 'otc_naperville' : m.id}>{m.shortName || m.name}</option>
                        ))}
                      </select>
                    </>
                  )}

                  <label>From:</label>
                  <input
                    type="date"
                    value={auditDateStart}
                    onChange={(e) => setAuditDateStart(e.target.value)}
                    className="al-filter-date"
                  />
                  <label>To:</label>
                  <input
                    type="date"
                    value={auditDateEnd}
                    onChange={(e) => setAuditDateEnd(e.target.value)}
                    className="al-filter-date"
                  />

                  <button className="al-filter-btn" onClick={handleAuditFilterApply} disabled={auditLoading}>
                    {auditLoading ? 'Loading...' : 'Apply Filters'}
                  </button>
                </div>
              </div>

              {/* Error */}
              {auditError && (
                <div className="al-error">Failed to load audit logs: {auditError}</div>
              )}

              {/* Loading (initial) */}
              {auditLoading && auditLogs.length === 0 && (
                <div className="al-loading">Loading audit logs...</div>
              )}

              {/* Empty state */}
              {!auditLoading && !auditError && auditLogs.length === 0 && (
                <div className="al-empty">No audit log entries found.</div>
              )}

              {/* Audit log table */}
              {auditLogs.length > 0 && (
                <>
                  <div className="al-table-wrapper">
                    <table className="al-table">
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>Action</th>
                          <th>User</th>
                          {isSuperAdmin && <th>Market</th>}
                          <th>Target</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.map(log => (
                          <tr key={log.id}>
                            <td className="al-cell-ts">
                              {log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                              }) : '—'}
                            </td>
                            <td>
                              <span className={`al-action-badge al-action-${(log.action || '').split('_')[0]}`}>
                                {(log.action || '').replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="al-cell-user">{log.userId || '—'}</td>
                            {isSuperAdmin && <td>{log.market || '—'}</td>}
                            <td className="al-cell-target">{log.target || '—'}</td>
                            <td className="al-cell-details">
                              {log.details ? (
                                <span className="al-details-text" title={JSON.stringify(log.details, null, 2)}>
                                  {typeof log.details === 'object'
                                    ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                                    : String(log.details)
                                  }
                                </span>
                              ) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Load More pagination */}
                  {auditHasMore && (
                    <div className="al-load-more">
                      <button
                        className="al-load-more-btn"
                        onClick={() => fetchAuditLogs(true)}
                        disabled={auditLoading}
                      >
                        {auditLoading ? 'Loading...' : 'Load More'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* VENUE TIER MANAGEMENT - Admin/Super Admin only (new auth) */}
        {/* ═══════════════════════════════════════════════════════ */}
        {useNewAuth && isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className="section-header-toggle"
            onClick={() => setIsVenueTierExpanded(!isVenueTierExpanded)}
          >
            <div className="section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Manage venue listing tiers (Basic, Silver, Gold, Platinum), featured badges, and priority placement in search results.">i</span> 🏷️ Venue Tiers & Placement</h2>
              {vtVenues.length > 0 && (
                <span className="section-badge">{vtVenues.length} venue{vtVenues.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            <button className="collapse-button">
              {isVenueTierExpanded ? '▼' : '▶'}
            </button>
          </div>

          {isVenueTierExpanded && (
            <div className="section-content">
              {/* Error */}
              {vtError && (
                <div className="vt-error">Failed to load venues: {vtError}</div>
              )}

              {/* Loading */}
              {vtLoading && (
                <div className="vt-loading">Loading venues...</div>
              )}

              {/* Empty state */}
              {!vtLoading && !vtError && vtVenues.length === 0 && (
                <div className="vt-empty">No venues found for this market.</div>
              )}

              {/* Venue tier table */}
              {!vtLoading && vtVenues.length > 0 && (
                <div className="vt-table-wrapper">
                  <table className="vt-table">
                    <thead>
                      <tr>
                        <th>Venue Name</th>
                        <th>Tier</th>
                        <th>Priority</th>
                        <th>Featured</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {vtVenues.map(venue => {
                        const edits = vtEdits[venue.id] || {};
                        const currentTier = edits.tier !== undefined ? edits.tier : (venue.tier || '');
                        const currentPriority = edits.priority !== undefined ? edits.priority : (venue.priority || 0);
                        const currentFeatured = edits.isFeatured !== undefined ? edits.isFeatured : (venue.isFeatured || false);
                        const hasEdits = edits && Object.keys(edits).length > 0;

                        return (
                          <tr key={venue.id}>
                            <td className="vt-cell-name">
                              {venue.name}
                              {venue.city && <span className="vt-city">{venue.city}</span>}
                            </td>
                            <td>
                              <select
                                value={currentTier}
                                onChange={(e) => handleVtEdit(venue.id, 'tier', e.target.value)}
                                className="vt-select"
                              >
                                <option value="">None</option>
                                <option value="Basic">Basic / Free</option>
                                <option value="Silver">Silver</option>
                                <option value="Gold">Gold</option>
                                <option value="Platinum">Platinum</option>
                              </select>
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={currentPriority}
                                onChange={(e) => handleVtEdit(venue.id, 'priority', parseInt(e.target.value, 10) || 0)}
                                className="vt-priority-input"
                              />
                            </td>
                            <td className="vt-cell-featured">
                              <input
                                type="checkbox"
                                checked={currentFeatured}
                                onChange={(e) => handleVtEdit(venue.id, 'isFeatured', e.target.checked)}
                                className="vt-checkbox"
                              />
                            </td>
                            <td>
                              <button
                                className={`vt-save-btn${hasEdits ? ' vt-save-active' : ''}`}
                                onClick={() => handleVtSave(venue)}
                                disabled={!hasEdits || vtSaving === venue.id}
                              >
                                {vtSaving === venue.id ? 'Saving...' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* USER MANAGEMENT SECTION - Admin/Super Admin only (new auth) */}
        {/* ═══════════════════════════════════════════════════════ */}
        {useNewAuth && isAdmin && (
        <section className="admin-section collapsible-section">
          <div
            className="section-header-toggle"
            onClick={() => setIsUserMgmtExpanded(!isUserMgmtExpanded)}
          >
            <div className="section-header-left">
              <h2><span className="ac-info-icon" data-tooltip="Create, edit, and deactivate user accounts. Assign roles (admin, venue owner), markets, and account tiers.">i</span> 👥 User Management</h2>
              <span className="section-badge">{userMgmtUsers.length} user{userMgmtUsers.length !== 1 ? 's' : ''}</span>
            </div>
            <button className="collapse-button">
              {isUserMgmtExpanded ? '▼' : '▶'}
            </button>
          </div>

          {isUserMgmtExpanded && (
            <div className="section-content">
              {/* Sub-nav: List / Create */}
              <div className="um-subnav">
                <button
                  className={`um-subnav-btn ${userMgmtView === 'list' ? 'active' : ''}`}
                  onClick={() => { setUserMgmtView('list'); resetUserForm(); setUserMgmtEditTarget(null); }}
                >
                  User List
                </button>
                <button
                  className={`um-subnav-btn ${userMgmtView === 'create' ? 'active' : ''}`}
                  onClick={() => { setUserMgmtView('create'); resetUserForm(); }}
                >
                  + Create User
                </button>
                {userMgmtView === 'edit' && (
                  <button className="um-subnav-btn active" disabled>
                    Edit User
                  </button>
                )}
              </div>

              {/* ─── User List View ─── */}
              {userMgmtView === 'list' && (
                <div className="um-list-view">
                  {/* Filters */}
                  <div className="um-filters">
                    {isSuperAdmin && (
                      <select
                        value={userMgmtFilterRole}
                        onChange={(e) => setUserMgmtFilterRole(e.target.value)}
                        className="um-filter-select"
                      >
                        <option value="">All Roles</option>
                        <option value="super_admin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="venue_owner">Venue Owner</option>
                      </select>
                    )}
                    <select
                      value={userMgmtFilterStatus}
                      onChange={(e) => setUserMgmtFilterStatus(e.target.value)}
                      className="um-filter-select"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="deactivated">Deactivated</option>
                    </select>
                    <button
                      className="um-refresh-btn"
                      onClick={fetchUsers}
                      disabled={userMgmtLoading}
                    >
                      {userMgmtLoading ? 'Loading...' : '🔄 Refresh'}
                    </button>
                  </div>

                  {/* Error */}
                  {userMgmtError && (
                    <div className="um-error">⚠️ {userMgmtError}</div>
                  )}

                  {/* Loading */}
                  {userMgmtLoading && !userMgmtError && (
                    <div className="um-loading">
                      <div className="spinner"></div>
                      <p>Loading users...</p>
                    </div>
                  )}

                  {/* User Table */}
                  {!userMgmtLoading && !userMgmtError && (
                    <div className="um-table-wrapper">
                      {userMgmtUsers.length === 0 ? (
                        <div className="um-empty">No users found.</div>
                      ) : (
                        <table className="um-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Markets</th>
                              <th>Status</th>
                              <th>Last Login</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userMgmtUsers.map((user) => (
                              <tr key={user.id} className={user.status === 'deactivated' ? 'um-row-deactivated' : ''}>
                                <td className="um-cell-name">{user.name || '—'}</td>
                                <td className="um-cell-email">{user.email || '—'}</td>
                                <td>
                                  <span className={`um-role-badge um-role-${user.role}`}>
                                    {user.role === 'super_admin' ? 'Super Admin' :
                                     user.role === 'admin' ? 'Admin' : 'Venue Owner'}
                                  </span>
                                </td>
                                <td className="um-cell-markets">
                                  {(user.markets || []).map(m => (
                                    <span key={m} className="um-market-tag">{m}</span>
                                  ))}
                                </td>
                                <td>
                                  <span className={`um-status-badge um-status-${user.status || 'active'}`}>
                                    {user.status || 'active'}
                                  </span>
                                </td>
                                <td className="um-cell-date">
                                  {user.last_login
                                    ? new Date(user.last_login).toLocaleDateString()
                                    : 'Never'}
                                </td>
                                <td className="um-cell-actions">
                                  <button
                                    className="um-action-btn um-edit-btn"
                                    onClick={() => startEditUser(user)}
                                    title="Edit user"
                                  >
                                    ✏️
                                  </button>
                                  {user.status !== 'deactivated' && user.id !== authUser?.id && (
                                    <button
                                      className="um-action-btn um-deactivate-btn"
                                      onClick={() => handleDeactivateUser(user.id, user.name || user.email)}
                                      title="Deactivate user"
                                    >
                                      🚫
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ─── Create User Form ─── */}
              {userMgmtView === 'create' && (
                <div className="um-form-view">
                  <h3>Create New User</h3>
                  <form onSubmit={handleCreateUser} className="um-form">
                    <div className="um-form-row">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={umFormName}
                        onChange={(e) => setUmFormName(e.target.value)}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div className="um-form-row">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={umFormEmail}
                        onChange={(e) => setUmFormEmail(e.target.value)}
                        placeholder="user@example.com"
                        required
                      />
                    </div>
                    <div className="um-form-row">
                      <label>Temporary Password *</label>
                      <input
                        type="text"
                        value={umFormPassword}
                        onChange={(e) => setUmFormPassword(e.target.value)}
                        placeholder="Min 8 characters (user changes on first login)"
                        required
                        minLength={8}
                      />
                    </div>
                    <div className="um-form-row">
                      <label>Role</label>
                      <select value={umFormRole} onChange={(e) => setUmFormRole(e.target.value)}>
                        <option value="venue_owner">Venue Owner</option>
                        {isSuperAdmin && <option value="admin">Admin</option>}
                        {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                      </select>
                    </div>
                    <div className="um-form-row">
                      <label>Markets</label>
                      <div className="um-market-checkboxes">
                        {(isSuperAdmin
                          ? ['tcc', 'otc_naperville']
                          : (authUser?.markets || [])
                        ).map(m => (
                          <label key={m} className="um-checkbox-label">
                            <input
                              type="checkbox"
                              checked={umFormMarkets.includes(m)}
                              onChange={() => handleMarketToggle(m)}
                            />
                            {m === 'tcc' ? 'TCC' : m === 'otc_naperville' ? 'OTC Naperville' : m}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="um-form-row">
                      <label>Account Tier</label>
                      <select value={umFormTier} onChange={(e) => setUmFormTier(e.target.value)}>
                        <option value="free">Free</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>
                    <div className="um-form-row">
                      <label>Venues (comma-separated IDs)</label>
                      <input
                        type="text"
                        value={umFormVenues}
                        onChange={(e) => setUmFormVenues(e.target.value)}
                        placeholder="rec123, rec456"
                      />
                    </div>
                    <div className="um-form-row">
                      <label>Notes</label>
                      <textarea
                        value={umFormNotes}
                        onChange={(e) => setUmFormNotes(e.target.value)}
                        placeholder="Internal notes about this user"
                        rows={2}
                      />
                    </div>

                    {umFormError && <div className="um-form-error">{umFormError}</div>}

                    <div className="um-form-actions">
                      <button type="submit" className="um-save-btn" disabled={umFormSaving}>
                        {umFormSaving ? 'Creating...' : 'Create User'}
                      </button>
                      <button
                        type="button"
                        className="um-cancel-btn"
                        onClick={() => { setUserMgmtView('list'); resetUserForm(); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* ─── Edit User Form ─── */}
              {userMgmtView === 'edit' && userMgmtEditTarget && (
                <div className="um-form-view">
                  <h3>Edit User: {userMgmtEditTarget.name || userMgmtEditTarget.email}</h3>
                  <form onSubmit={handleEditUser} className="um-form">
                    <div className="um-form-row">
                      <label>Name</label>
                      <input
                        type="text"
                        value={umFormName}
                        onChange={(e) => setUmFormName(e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div className="um-form-row">
                      <label>Email</label>
                      <input
                        type="email"
                        value={umFormEmail}
                        disabled
                        className="um-input-disabled"
                      />
                      <span className="um-field-note">Email cannot be changed</span>
                    </div>
                    <div className="um-form-row">
                      <label>Reset Password</label>
                      <input
                        type="text"
                        value={umFormPassword}
                        onChange={(e) => setUmFormPassword(e.target.value)}
                        placeholder="Leave blank to keep current"
                      />
                    </div>
                    {isSuperAdmin && (
                      <div className="um-form-row">
                        <label>Role</label>
                        <select value={umFormRole} onChange={(e) => setUmFormRole(e.target.value)}>
                          <option value="venue_owner">Venue Owner</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </div>
                    )}
                    <div className="um-form-row">
                      <label>Markets</label>
                      <div className="um-market-checkboxes">
                        {(isSuperAdmin
                          ? ['tcc', 'otc_naperville']
                          : (authUser?.markets || [])
                        ).map(m => (
                          <label key={m} className="um-checkbox-label">
                            <input
                              type="checkbox"
                              checked={umFormMarkets.includes(m)}
                              onChange={() => handleMarketToggle(m)}
                            />
                            {m === 'tcc' ? 'TCC' : m === 'otc_naperville' ? 'OTC Naperville' : m}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="um-form-row">
                      <label>Account Tier</label>
                      <select value={umFormTier} onChange={(e) => setUmFormTier(e.target.value)}>
                        <option value="free">Free</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>
                    <div className="um-form-row">
                      <label>Status</label>
                      <select value={umFormStatus} onChange={(e) => setUmFormStatus(e.target.value)}>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="deactivated">Deactivated</option>
                      </select>
                    </div>
                    <div className="um-form-row">
                      <label>Venues (comma-separated IDs)</label>
                      <input
                        type="text"
                        value={umFormVenues}
                        onChange={(e) => setUmFormVenues(e.target.value)}
                        placeholder="rec123, rec456"
                      />
                    </div>
                    <div className="um-form-row">
                      <label>Notes</label>
                      <textarea
                        value={umFormNotes}
                        onChange={(e) => setUmFormNotes(e.target.value)}
                        placeholder="Internal notes"
                        rows={2}
                      />
                    </div>

                    {umFormError && <div className="um-form-error">{umFormError}</div>}

                    <div className="um-form-actions">
                      <button type="submit" className="um-save-btn" disabled={umFormSaving}>
                        {umFormSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        className="um-cancel-btn"
                        onClick={() => { setUserMgmtView('list'); setUserMgmtEditTarget(null); resetUserForm(); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </section>
        )}

      </div>
    </div>
  );
}

export default AdminConsole;
